import * as THREE from 'three';
import { brushAabb, brushHeightAt, type Brush } from '../physics/Brush';
import type { CapsuleBody, CapsuleWorld } from '../physics/CapsuleWorld';
import type { PlayerController } from '../player/PlayerController';
import type { AudioManager } from '../audio/AudioManager';
import type { DoorSystem } from '../world/DoorSystem';
import type { FacilityRoom } from '../world/FacilityDressing';
import { adjacentRooms, listRoomPortals, roomAt } from '../world/RoomCuller';
import { clearNavFollow, createNavFollow, FacilityNav, type NavFollow } from './EnemyNav';
import type { WeaponId } from '../weapons/WeaponDefs';
import { claspSupportHand } from '../player/TpWeaponAttach';
import {
  attachFpsWorldHold,
  createFpsWorldHold,
  punchProgress,
  syncFpsWorldHold,
  type FpsWorldHold,
} from '../player/FpsWorldHold';
import type { ShotLineKind, ShotLineSystem } from '../world/ShotLines';
import type { AlarmSystem } from '../world/AlarmSystem';
import {
  addRagdollImpulse,
  collapseEnemyVisual,
  createRagdoll,
  ragdollHips,
  stepRagdollPhysics,
  writeRagdollBones,
  type EnemyRagdoll,
} from './EnemyRagdoll';
import {
  applyProcEnemyPose,
  buildProcEnemy,
  cloneEnemyFace,
  createProcEnemyKit,
  pickCharLook,
  pickEnemyVariant,
  pickGuardHeadgear,
  procClipSet,
  setEnemyFaceMood,
  setEnemyMouthFrame,
  tickEnemyBlink,
  type EnemyFace,
  type EnemyVariantId,
  type HeadgearId,
  type MouthFrame,
  type ProcEnemyKit,
} from './ProcEnemy';
import {
  bakedPunchClip,
  nextPunchCombo,
  punchImpactT,
  punchPlayDuration,
  punchSwingRate,
  type PunchClipId,
} from '../player/PunchClip';

export type EnemyKind = 'pistol' | 'shotgun' | 'melee' | 'mp5' | 'ak47' | 'sawedoff' | 'scientist' | 'civilian';

export interface EnemySpawn {
  kind: EnemyKind;
  position: THREE.Vector3;
  yaw?: number;
  /** Sitting on a WC — waits until the player opens that stall. */
  script?: 'toiletSit';
  toiletDoorId?: string;
}

/** `attack` = open-field fight (GE global 0006 “Take a shot”). */
type AiState =
  | 'roam'
  | 'chase'
  | 'attack'
  | 'hurt'
  | 'dead'
  | 'seekCover'
  | 'cover'
  | 'peek'
  | 'duck'
  | 'flee'
  | 'alarm';
type AttackPhase = 'none' | 'aim' | 'windup' | 'swing' | 'shoot';

/**
 * Discrete open-combat moves from GoldenEye global AI list 0006
 * (n64decomp/007 + gepd function explorer).
 */
type GeCombatMove =
  | 'none'
  | 'standShoot'
  | 'kneelShoot'
  | 'sidestep'
  | 'hop'
  | 'runSide'
  | 'advanceWalk'
  | 'advanceRun';

interface EnemyDef {
  hp: number;
  walkSpeed: number;
  runSpeed: number;
  attackRange: number;
  preferRange: number;
  damage: number;
  fireInterval: number;
  aggroRange: number;
  alertSfx: string;
  hurtSfx: string;
  deathSfx: string;
}

const DEFS: Record<EnemyKind, EnemyDef> = {
  pistol: {
    hp: 40,
    walkSpeed: 2.4,
    runSpeed: 4.2,
    attackRange: 18,
    preferRange: 10,
    damage: 8,
    /** One deliberate shot, then pause (not a burst). */
    fireInterval: 2.4,
    aggroRange: 28,
    alertSfx: 'enemyAlert',
    hurtSfx: 'enemyHurt',
    deathSfx: 'enemyDeath',
  },
  shotgun: {
    hp: 60,
    walkSpeed: 2.1,
    runSpeed: 3.6,
    attackRange: 12,
    preferRange: 6,
    damage: 18,
    fireInterval: 3.2,
    aggroRange: 26,
    alertSfx: 'enemyAlert',
    hurtSfx: 'enemyHurt',
    deathSfx: 'enemyDeath',
  },
  melee: {
    hp: 110,
    walkSpeed: 2.8,
    runSpeed: 5.4,
    attackRange: 1.65,
    preferRange: 1.2,
    damage: 22,
    fireInterval: 1.25,
    aggroRange: 24,
    alertSfx: 'enemyAlert',
    hurtSfx: 'enemyHurt',
    deathSfx: 'enemyDeath',
  },
  mp5: {
    hp: 45,
    walkSpeed: 2.5,
    runSpeed: 4.4,
    attackRange: 16,
    preferRange: 8,
    damage: 6,
    fireInterval: 0.11,
    aggroRange: 28,
    alertSfx: 'enemyAlert',
    hurtSfx: 'enemyHurt',
    deathSfx: 'enemyDeath',
  },
  ak47: {
    hp: 55,
    walkSpeed: 2.3,
    runSpeed: 4.0,
    attackRange: 20,
    preferRange: 12,
    damage: 10,
    fireInterval: 0.13,
    aggroRange: 30,
    alertSfx: 'enemyAlert',
    hurtSfx: 'enemyHurt',
    deathSfx: 'enemyDeath',
  },
  sawedoff: {
    hp: 55,
    walkSpeed: 2.2,
    runSpeed: 3.7,
    attackRange: 8,
    preferRange: 4.5,
    damage: 16,
    fireInterval: 2.8,
    aggroRange: 22,
    alertSfx: 'enemyAlert',
    hurtSfx: 'enemyHurt',
    deathSfx: 'enemyDeath',
  },
  scientist: {
    hp: 28,
    walkSpeed: 1.7,
    runSpeed: 4.6,
    attackRange: 0,
    preferRange: 10,
    damage: 0,
    fireInterval: 99,
    aggroRange: 0,
    alertSfx: 'enemyAlert',
    hurtSfx: 'enemyHurt',
    deathSfx: 'enemyDeath',
  },
  civilian: {
    hp: 26,
    walkSpeed: 1.8,
    runSpeed: 4.8,
    attackRange: 0,
    preferRange: 10,
    damage: 0,
    fireInterval: 99,
    aggroRange: 0,
    alertSfx: 'enemyAlert',
    hurtSfx: 'enemyHurt',
    deathSfx: 'enemyDeath',
  },
};

const ROAM_RADIUS = 4.5;
/** Vision: 90° cone (cos 45°) + LOS. Crouched player is harder to spot. */
const SIGHT_COS = Math.cos((45 * Math.PI) / 180);
const SIGHT_RANGE = 18;
const SIGHT_RANGE_CROUCH = 11;
const SIGHT_BUMP = 1.6;
/** Footstep hearing (no cone). Gunshot is louder. */
const HEAR_CROUCH = 2.2;
const HEAR_WALK = 6;
const HEAR_SPRINT = 12;
const HEAR_GUNSHOT = 16;
const HEAR_GUNSHOT_FRIENDLY = 12;
const HEAR_GUNSHOT_EXPLORE = 10;
/** Same room or through one doorway — not two rooms over. */
const HEAR_GUNSHOT_HOPS = 1;
/** Silenced muzzle blast inside this range is obviously a gunshot — full aggro. */
const SILENCED_ALERT = 3.7;
/** Allies hear an Ow / hit bark and join the fight. */
const HEAR_HURT = 9;
const HEAR_SMASH = 10;
/** Fist on a wall or door — same room, close only. */
const HEAR_THUD = 7;
const INVESTIGATE_TIME = 4.5;
const INVESTIGATE_SHOT = 7;
const LOSE_SIGHT_TIME = 7;
const BARK_COOLDOWN = 4;
const TAUNT_AFTER = 6;
const COME_OUT_KEYS = new Set(['voice_shotgun_2']);
/** GE sidestep / run-sideways speeds (m/s). Narrow halls → slightly slower. */
const SIDESTEP_SPEED = 1.2;
const RUN_SIDE_SPEED = 2.2;
/** GE 0006: big moves (run side / charge) gated ~3s apart. */
const GE_ACTION_BUSY = 3.0;
/** GE charge gate — walk/run & fire only beyond this range (shorter in corridors). */
const GE_CHARGE_DIST = 8;
const GUN_AIM_HOLD = 0.45;
const SHOOT_LOCK = 0.55;
const FADE_LOCO = 0.32;
const FADE_COMBAT = 0.22;
const FADE_HURT = 0.16;

const ENEMY_STAND_HEIGHT = 1.75;
const ENEMY_DUCK_HEIGHT = 1.15;
const ENEMY_DUCK_SINK = 0.32;
/** Narrow Facility halls — don't seek cover across half the map. */
const COVER_SEARCH_RANGE = 7;
const COVER_ABANDON_DIST = 2.6;
const PEEK_OFFSET = 0.7;

const LOOK_YAW_MAX = 0.55;
const LOOK_PITCH_MAX = 0.35;
const LOOK_SPEED = 5.5;
const LOOK_NECK_WEIGHT = 0.4;
/** Extra local head yaw. */
const LOOK_YAW_BIAS = 0;

interface EnemyRuntime {
  id: number;
  kind: EnemyKind;
  def: EnemyDef;
  root: THREE.Group;
  model: THREE.Object3D;
  actions: Set<string>;
  /** Full-body clip name, or `upper+lower` when layered. */
  currentClip: string;
  upperClip: string;
  lowerClip: string;
  animT: number;
  /** When set, gunner uses upper/lower split (aim + walk legs). */
  layerMode: 'full' | 'aimloco';
  body: CapsuleBody;
  yaw: number;
  hp: number;
  state: AiState;
  stateT: number;
  attackCd: number;
  /** Blocks loco/idle from overwriting one-shot combat anims. */
  attackLockT: number;
  attackPhase: AttackPhase;
  pendingDamage: boolean;
  punchId: PunchClipId;
  punchComboStep: number;
  hurtT: number;
  /** Suppress retriggering full-body hurt clips (calf spam). */
  hurtCooldown: number;
  alerted: boolean;
  dead: boolean;
  home: THREE.Vector3;
  roamTarget: THREE.Vector3;
  roamWaitT: number;
  roamFidget: boolean;
  strafeSign: number;
  strafeT: number;
  lookHead: THREE.Object3D | null;
  lookNeck: THREE.Object3D | null;
  lookHeadBind: THREE.Quaternion;
  lookNeckBind: THREE.Quaternion;
  lookYaw: number;
  lookPitch: number;
  /** Support-hand clasp (runtime CCD toward gun). */
  leftHand: THREE.Object3D | null;
  leftForeArm: THREE.Object3D | null;
  leftArm: THREE.Object3D | null;
  rightHand: THREE.Object3D | null;
  /** Debug: skip AI movement / attacks. */
  frozen: boolean;
  weapon: THREE.Object3D | null;
  /** Clone-style FPS hold (ProcArms + jacket bridges). */
  fpsHold: FpsWorldHold | null;
  /** Smoothed hip→ADS for the FPS hold. */
  fpsAds: number;
  /** Seconds left raising the gun to the eye (fire window + a short hold). */
  adsHoldT: number;
  /** Hand-local socket rest pose (recoil returns here). */
  weaponBasePos: THREE.Vector3;
  weaponBaseRot: THREE.Euler;
  /** 1 → 0 kick envelope after a shot. */
  weaponRecoil: number;
  /** Shots remaining in current open-fight / peek burst. */
  burstLeft: number;
  /** Pause after a burst before the next volley. */
  burstPauseT: number;
  /** Time left ducked in the open. */
  duckT: number;
  /** Timer until next GE attack pick while fighting. */
  combatDecisionT: number;
  /** Active GE 0006-style combat move. */
  combatMove: GeCombatMove;
  /** Seconds left on the current combat move. */
  combatMoveT: number;
  /** GE “hasn’t begun anything in the last 3s” gate. */
  geActionBusyT: number;
  /** Brief GE look-about after alert (noise / first sight). */
  lookaroundT: number;
  /** Seconds left walking toward a heard noise (not combat). */
  investigateT: number;
  heardPos: THREE.Vector3;
  /** Already reacted to gunfire — later shots retarget, they don't flinch again. */
  heardGunshot: boolean;
  /** Last seen player / last guessed shot — chase this when LOS is gone. */
  lastKnown: THREE.Vector3;
  nav: NavFollow;
  ducked: boolean;
  /** Smoothed model sink while ducked / kneeling. */
  duckSink: number;
  kneelBlend: number;
  standHeight: number;
  duckHeight: number;
  /** Model Y from normalizeCharacter (feet on ground). */
  modelBaseY: number;
  /** Prefer seeking cover after being hit. */
  coverWanted: boolean;
  coverBrushId: string | null;
  /** Current nav target while seeking (approach hop or hide). */
  coverPos: THREE.Vector3;
  /** Final hide point (may differ from coverPos until last hop). */
  coverHidePos: THREE.Vector3;
  peekPos: THREE.Vector3;
  /** Hold / peek phase timer. */
  coverT: number;
  /** Short cover (sandbag/desk) — stay ducked while hidden. */
  coverIsShort: boolean;
  /** After a failed/abandoned seek, suppress cover pulls briefly. */
  coverCooldownT: number;
  /** Position sample for stuck-seek detection. */
  coverCheckPos: THREE.Vector3;
  coverCheckT: number;
  ragdoll: EnemyRagdoll | null;
  bloodMarks: THREE.Mesh[];
  hurtOverlay: { zone: 'foot' | 'arm'; side: 'L' | 'R'; t: number; duration: number } | null;
  rightArm: THREE.Object3D | null;
  rightForeArm: THREE.Object3D | null;
  leftFoot: THREE.Object3D | null;
  rightFoot: THREE.Object3D | null;
  /** Hysteresis for aim-walk vs plant so legs don't flip every frame. */
  aimLocoWalk: boolean;
  breathT: number;
  breathSpine: THREE.Object3D | null;
  /** Damped lateral lean (radians), applied after mixer. */
  spineLean: number;
  variant: EnemyVariantId;
  face: EnemyFace | null;
  lostSightT: number;
  talkT: number;
  talkMouthT: number;
  barkCd: number;
  pointT: number;
  pendingLookaround: boolean;
  lastBark: string;
  stopTalk: (() => void) | null;
  combatAge: number;
  fleeing: boolean;
  brokeAndRan: boolean;
  hurtBarkCd: number;
  handsUpSaid: boolean;
  fistsTauntSaid: boolean;
  sitBlend: number;
  toiletPhase: 'none' | 'sit' | 'occupied' | 'recognize' | 'flush' | 'stand' | 'noise' | 'rise' | 'leave';
  toiletT: number;
  toiletBayX: number;
  toiletDoorId: string;
  headgear: HeadgearId;
  hat: THREE.Object3D | null;
  helmet: THREE.Object3D | null;
  /** Hat/helmet was shot off — a later head hit is lethal. */
  headgearRemoved: boolean;
  /** This shot only plucked gear; skip hearShot so an unseen guard stays unaware. */
  quietGearKnock: boolean;
  alarmRun: boolean;
}

/** Procedural RightHand: +Y = fingers, +Z = back of hand. Prop +Z is barrel. */
export const PISTOL_SOCKET = {
  position: new THREE.Vector3(0.0, 0.078, 0.018),
  rotation: new THREE.Euler(-Math.PI / 2, Math.PI, 0, 'YXZ'),
};
export const SHOTGUN_SOCKET = {
  position: new THREE.Vector3(0.0, 0.085, 0.02),
  rotation: new THREE.Euler(-Math.PI / 2, Math.PI, 0.08, 'YXZ'),
};
export const MP5_SOCKET = {
  position: new THREE.Vector3(0.0, 0.08, 0.02),
  rotation: new THREE.Euler(-Math.PI / 2, Math.PI, 0.04, 'YXZ'),
};
export const AK_SOCKET = {
  position: new THREE.Vector3(0.0, 0.085, 0.022),
  rotation: new THREE.Euler(-Math.PI / 2, Math.PI, 0.06, 'YXZ'),
};

const VOICE_SETS: Record<string, string[]> = {
  pistol: ['voice_pistol_0', 'voice_pistol_1', 'voice_pistol_2', 'voice_pistol_3', 'voice_pistol_4'],
  shotgun: ['voice_shotgun_0', 'voice_shotgun_1', 'voice_shotgun_3'],
  melee: ['voice_melee_0', 'voice_melee_1', 'voice_melee_2', 'voice_melee_3'],
  scientist: [
    'voice_friendly_sci_0',
    'voice_friendly_sci_1',
    'voice_friendly_sci_2',
    'voice_friendly_sci_3',
    'voice_friendly_sci_4',
    'voice_friendly_sci_5',
    'voice_friendly_sci_6',
    'voice_friendly_sci_7',
    'voice_friendly_sci_8',
  ],
  civilian: [
    'voice_friendly_civ_0',
    'voice_friendly_civ_1',
    'voice_friendly_civ_2',
    'voice_friendly_civ_3',
    'voice_friendly_civ_4',
    'voice_friendly_civ_5',
    'voice_friendly_civ_6',
  ],
  mp5: ['voice_pistol_0', 'voice_pistol_1', 'voice_pistol_2', 'voice_pistol_3', 'voice_pistol_4'],
  ak47: ['voice_pistol_0', 'voice_pistol_1', 'voice_pistol_2', 'voice_pistol_3', 'voice_pistol_4'],
  sawedoff: ['voice_shotgun_0', 'voice_shotgun_1', 'voice_shotgun_3'],
  come_out: ['voice_shotgun_2'],
  spotted: ['voice_spotted'],
  hurt_pistol: [
    'voice_hurt_pistol_0',
    'voice_hurt_pistol_1',
    'voice_hurt_pistol_2',
    'voice_hurt_pistol_3',
    'voice_hurt_pistol_4',
  ],
  hurt_shotgun: ['voice_hurt_shotgun_0', 'voice_hurt_shotgun_1', 'voice_hurt_shotgun_2', 'voice_hurt_shotgun_3'],
  hurt_melee: ['voice_hurt_melee_0', 'voice_hurt_melee_1', 'voice_hurt_melee_2', 'voice_hurt_melee_3'],
  flee: ['voice_flee_0', 'voice_flee_1', 'voice_flee_2', 'voice_flee_3', 'voice_flee_4'],
  boxing: ['voice_boxing_0', 'voice_boxing_1', 'voice_boxing_2', 'voice_boxing_3'],
  melee_box: ['voice_melee_box_0', 'voice_melee_box_1', 'voice_melee_box_2', 'voice_melee_box_3'],
  curious: ['voice_curious_0', 'voice_curious_1', 'voice_curious_2', 'voice_curious_3'],
  scared_shot: ['voice_scared_shot_0', 'voice_scared_shot_1', 'voice_scared_shot_2', 'voice_scared_shot_3'],
  toilet_shot: ['voice_toilet_shot_0', 'voice_toilet_shot_1'],
  toilet_noise: ['voice_toilet_noise'],
  silenced_close: [
    'voice_silenced_close_0',
    'voice_silenced_close_1',
    'voice_silenced_close_2',
  ],
  hat_spot: ['voice_hat_0', 'voice_hat_1', 'voice_hat_2', 'voice_hat_3'],
  sound_alarm: ['voice_alarm_0', 'voice_alarm_1'],
  civ_talk: [
    'voice_civ_talk_0',
    'voice_civ_talk_1',
    'voice_civ_talk_2',
    'voice_civ_talk_3',
    'voice_civ_talk_4',
    'voice_civ_talk_5',
    'voice_civ_talk_6',
    'voice_civ_talk_7',
  ],
  sci_talk: [
    'voice_sci_talk_0',
    'voice_sci_talk_1',
    'voice_sci_talk_2',
    'voice_sci_talk_3',
    'voice_sci_talk_4',
    'voice_sci_talk_5',
  ],
};

const VOICE_TEXT: Record<string, string> = {
  voice_pistol_0: 'You there! Stop!',
  voice_pistol_1: 'Intruder! Open fire!',
  voice_pistol_2: 'I see you!',
  voice_pistol_3: "You're not supposed to be here!",
  voice_pistol_4: "Don't move!",
  voice_shotgun_0: 'Get out of my facility!',
  voice_shotgun_1: "I'll blow you apart!",
  voice_shotgun_2: 'Come out, come out wherever you are!',
  voice_shotgun_3: 'You picked the wrong hall!',
  voice_spotted: 'Aha, there you are!',
  voice_melee_0: 'Come here!',
  voice_melee_1: "I'll tear you apart!",
  voice_melee_2: 'You dare enter my territory? Leave now!',
  voice_melee_3: 'Get over here!',
  voice_scientist_0: "Security! There's someone in the lab!",
  voice_scientist_1: 'Stay back! Stay back!',
  voice_hurt_pistol_0: 'Ow!',
  voice_hurt_pistol_1: 'Ugh!',
  voice_hurt_pistol_2: 'Aagh!',
  voice_hurt_pistol_3: "I'm hit!",
  voice_hurt_pistol_4: 'Gah!',
  voice_hurt_shotgun_0: 'Ow!',
  voice_hurt_shotgun_1: 'Ugh!',
  voice_hurt_shotgun_2: 'Aagh!',
  voice_hurt_shotgun_3: "I'm hit!",
  voice_hurt_melee_0: 'Ow!',
  voice_hurt_melee_1: 'Ugh!',
  voice_hurt_melee_2: 'Aagh!',
  voice_hurt_melee_3: 'Gah!',
  voice_flee_0: 'Run away!',
  voice_flee_1: "Screw this, evil boss doesn't pay me nuff' for this shit",
  voice_flee_2: "I'm out!",
  voice_flee_3: 'Not worth dying for!',
  voice_flee_4: 'Fall back! I quit!',
  voice_friendly_sci_0: 'This guy is a maniac!',
  voice_friendly_sci_1: "Hey, I'm on your side!",
  voice_friendly_sci_2: 'What are you doing?',
  voice_friendly_sci_3: 'Help!',
  voice_friendly_sci_4: "You're meant to rescue me, not shoot me!",
  voice_friendly_sci_5: "Don't shoot! I'm unarmed!",
  voice_friendly_sci_6: 'I thought you were here to get us out!',
  voice_friendly_sci_7: 'Please, I work in the lab!',
  voice_friendly_sci_8: "I can open doors, just don't kill me!",
  voice_friendly_civ_0: 'This guy is a maniac!',
  voice_friendly_civ_1: 'What are you doing?',
  voice_friendly_civ_2: 'Help!',
  voice_friendly_civ_3: "You're the good guy, right?!",
  voice_friendly_civ_4: 'Stop! Civilian!',
  voice_friendly_civ_5: 'That hurt, you lunatic!',
  voice_friendly_civ_6: "Don't shoot! I'm unarmed!",
  voice_boxing_0: 'Oh we have a little Boxing Champion running around?',
  voice_boxing_1: 'Oh, someone wants their ass kicked!',
  voice_boxing_2: 'You wanna go, champ?',
  voice_boxing_3: 'Put those fists down!',
  voice_melee_box_0: "I'll beat you up!",
  voice_melee_box_1: "I'll put my fist in your face!",
  voice_melee_box_2: "Come on then, let's dance!",
  voice_melee_box_3: 'You and me, fists only!',
  voice_toilet_occupied: "Hey! Occupied! Shut the door, I'm taking a shit!",
  voice_toilet_recognize: "Wait... I don't recognize you.",
  voice_toilet_attack: "You're not one of us! Come here!",
  voice_toilet_shot_0: 'What was that?',
  voice_toilet_shot_1: 'Is someone shooting around here?',
  voice_toilet_noise: "Who's making all this noise?",
  voice_curious_0: 'What was that?',
  voice_curious_1: 'That sounded like a gunshot!',
  voice_curious_2: 'Did you hear that?',
  voice_curious_3: "I'm going to check it out.",
  voice_silenced_close_0: 'I think that was a bullet.',
  voice_silenced_close_1: 'That was a gunshot — close!',
  voice_silenced_close_2: 'Someone just fired!',
  voice_hat_0: 'Hey!',
  voice_hat_1: 'Intruder!',
  voice_hat_2: "I'll get you!",
  voice_hat_3: 'Oh shit!',
  voice_alarm_0: 'Sound the alarm!',
  voice_alarm_1: 'Sound the alarm!',
  voice_scared_shot_0: 'That was a gunshot!',
  voice_scared_shot_1: "Oh god, someone's shooting!",
  voice_scared_shot_2: 'What was that?!',
  voice_scared_shot_3: 'Stay down!',
  voice_civ_talk_0: 'I was just here for the vending machine.',
  voice_civ_talk_1: "Don't tell HR I talked to you.",
  voice_civ_talk_2: 'I thought this was a team-building day.',
  voice_civ_talk_3: 'Nice gun. Very legal looking.',
  voice_civ_talk_4: 'The coffee here could stop a tank.',
  voice_civ_talk_5: 'If anyone asks, I never saw you.',
  voice_civ_talk_6: 'I clocked out three hours ago.',
  voice_civ_talk_7: 'Are you the janitor? Because wow.',
  voice_sci_talk_0: "Please don't sneeze on the samples.",
  voice_sci_talk_1: "The reactor is fine. That's official.",
  voice_sci_talk_2: "I have a PhD and I still can't open that door.",
  voice_sci_talk_3: 'Are you here about the smell, or the screaming?',
  voice_sci_talk_4: 'I left my badge in another dimension.',
  voice_sci_talk_5: "If you see a glowing barrel, that's Dave's.",
};

export interface SpeechLookTip {
  label: string;
  x: number;
  y: number;
}

const _lookFwd = new THREE.Vector3();
const _lookBodyFwd = new THREE.Vector3();
const _lookTo = new THREE.Vector3();
const _lookFlat = new THREE.Vector3();
const TALK_DIST = 2.45;
const TALK_FACING = Math.cos((52 * Math.PI) / 180);

/** Default hangar spawns if arena does not supply any. */
export const DEFAULT_ENEMY_SPAWNS: EnemySpawn[] = [
  { kind: 'pistol', position: new THREE.Vector3(8, 0.05, -6), yaw: Math.PI * 0.25 },
  { kind: 'shotgun', position: new THREE.Vector3(22, 0.05, 10), yaw: -Math.PI * 0.5 },
  { kind: 'pistol', position: new THREE.Vector3(-14, 1.55, 1), yaw: Math.PI * 0.5 },
  { kind: 'pistol', position: new THREE.Vector3(-2, 0.05, 14), yaw: Math.PI },
  { kind: 'shotgun', position: new THREE.Vector3(12, 0.05, 4), yaw: -Math.PI * 0.2 },
];

/**
 * Animated PMC enemies. Gunner combat follows GoldenEye global AI list 0006
 * (“Take a shot”: sidestep / run-side / charge / stand|kneel) plus cover peel.
 */
export class EnemySystem {
  private readonly enemies: EnemyRuntime[] = [];
  private kit: ProcEnemyKit | null = null;
  private nextId = 1;
  private spawnCount = 0;
  private readonly looseHats: Array<{
    mesh: THREE.Object3D;
    vel: THREE.Vector3;
    spin: THREE.Vector3;
    life: number;
  }> = [];
  private readonly tmp = new THREE.Vector3();
  private readonly tmp2 = new THREE.Vector3();
  private readonly tmp3 = new THREE.Vector3();
  private readonly listenerPos = new THREE.Vector3();
  private readonly playerKnown = new THREE.Vector3();
  private readonly ray = new THREE.Raycaster();
  private ready = false;
  private playerFistsUp = false;
  private doors: DoorSystem | null = null;
  private alarms: AlarmSystem | null = null;
  private rooms: FacilityRoom[] = [];
  private visibleRooms: ReadonlySet<string> | null = null;
  private roomAdj = new Map<string, Set<string>>();
  private roomPortals: Array<{ from: string; to: string; box: THREE.Box3 }> = [];
  private readonly nav = new FacilityNav();
  private readonly hop = new THREE.Vector3();
  /** Hostile died — used for emergency medkit drops. */
  onHostileKilled: ((pos: THREE.Vector3) => void) | null = null;
  private shotLines: ShotLineSystem | null = null;

  constructor(
    private readonly scene: THREE.Scene,
    private readonly world: CapsuleWorld,
    private readonly audio: AudioManager,
  ) {}

  bindDoors(doors: DoorSystem): void {
    this.doors = doors;
  }

  bindAlarms(alarms: AlarmSystem): void {
    this.alarms = alarms;
  }

  setShotLines(sys: ShotLineSystem): void {
    this.shotLines = sys;
  }

  bindRooms(rooms: FacilityRoom[]): void {
    this.rooms = rooms;
    this.roomAdj = adjacentRooms(rooms);
    this.roomPortals = listRoomPortals(rooms);
    this.nav.build(rooms, this.world.brushes);
  }

  setVisibleRooms(ids: ReadonlySet<string> | null): void {
    this.visibleRooms = ids;
  }

  anyMoving(): boolean {
    for (const e of this.enemies) {
      if (e.dead) {
        if (e.ragdoll && !e.ragdoll.sleeping) return true;
        continue;
      }
      if (e.attackPhase !== 'none') return true;
      const spd = e.body.velocity.x * e.body.velocity.x + e.body.velocity.z * e.body.velocity.z;
      if (spd > 0.05) return true;
    }
    return false;
  }

  private shows(e: EnemyRuntime): boolean {
    if (!this.visibleRooms || this.visibleRooms.size === 0) return true;
    const r = roomAt(this.rooms, e.body.position);
    return !r || this.visibleRooms.has(r.id);
  }

  /** Keep the last known attacker (the player) current for damage-aggro. */
  notePlayer(player: PlayerController): void {
    this.playerKnown.copy(player.body.position);
    this.listenerPos.copy(player.camera.position);
  }

  collectCullRoots(out: THREE.Object3D[]): void {
    for (const e of this.enemies) out.push(e.root);
  }

  get isReady(): boolean {
    return this.ready;
  }

  /** World-projected captions for enemies who are currently speaking. */
  speechTips(camera: THREE.Camera, width: number, height: number): SpeechLookTip[] {
    if (width < 8 || height < 8) return [];
    const tips: SpeechLookTip[] = [];
    for (const e of this.enemies) {
      if (e.dead || e.talkT <= 0 || !e.lastBark) continue;
      const label = VOICE_TEXT[e.lastBark];
      if (!label) continue;
      const headY = e.body.position.y + (e.ducked ? 1.32 : 1.88);
      _speechWorld.set(e.body.position.x, headY, e.body.position.z);
      if (_speechWorld.distanceTo(camera.position) > 26) continue;
      _speechNdc.copy(_speechWorld).project(camera);
      if (_speechNdc.z < -1 || _speechNdc.z > 1) continue;
      if (_speechNdc.x < -1.2 || _speechNdc.x > 1.2) continue;
      if (_speechNdc.y < -1.25 || _speechNdc.y > 1.25) continue;
      tips.push({
        label,
        x: Math.min(width - 40, Math.max(40, (_speechNdc.x * 0.5 + 0.5) * width)),
        y: Math.min(height - 24, Math.max(22, (-_speechNdc.y * 0.5 + 0.5) * height)),
      });
    }
    return tips;
  }

  /** F-to-talk on a nearby civilian or scientist. Consumes the press even if they are mid-line. */
  tryTalk(player: PlayerController): boolean {
    const e = this.pickTalkTarget(player);
    if (!e) return false;
    if (e.talkT > 0.28) return true;
    const set = e.kind === 'scientist' ? VOICE_SETS.sci_talk! : VOICE_SETS.civ_talk!;
    this.playLine(e, set, true);
    const dx = player.body.position.x - e.body.position.x;
    const dz = player.body.position.z - e.body.position.z;
    if (dx * dx + dz * dz > 1e-6) e.yaw = Math.atan2(-dx, -dz);
    return true;
  }

  lookTalkTip(
    player: PlayerController,
    camera: THREE.Camera,
    width: number,
    height: number,
  ): SpeechLookTip | null {
    const e = this.pickTalkTarget(player);
    if (!e || e.talkT > 0.12 || width < 8 || height < 8) return null;
    const headY = e.body.position.y + (e.ducked ? 1.32 : 1.88);
    _speechWorld.set(e.body.position.x, headY, e.body.position.z);
    _speechNdc.copy(_speechWorld).project(camera);
    if (_speechNdc.z < -1 || _speechNdc.z > 1) return null;
    return {
      label: 'F - Talk',
      x: Math.min(width - 40, Math.max(40, (_speechNdc.x * 0.5 + 0.5) * width)),
      y: Math.min(height - 24, Math.max(22, (-_speechNdc.y * 0.5 + 0.5) * height)),
    };
  }

  private pickTalkTarget(player: PlayerController): EnemyRuntime | null {
    const pos = player.body.position;
    _lookFwd.set(-Math.sin(player.yaw), 0, -Math.cos(player.yaw));
    let best: EnemyRuntime | null = null;
    let bestScore = Infinity;
    for (const e of this.enemies) {
      if (e.dead || e.fleeing || !isFriendlyKind(e.kind)) continue;
      if (e.toiletPhase !== 'none') continue;
      _lookTo.set(e.body.position.x - pos.x, 0, e.body.position.z - pos.z);
      const dist = _lookTo.length();
      if (dist > TALK_DIST || dist < 1e-4) continue;
      _lookTo.multiplyScalar(1 / dist);
      const facing = _lookFwd.dot(_lookTo);
      if (facing < TALK_FACING && dist > 0.9) continue;
      const score = dist - facing * 0.4;
      if (score < bestScore) {
        bestScore = score;
        best = e;
      }
    }
    return best;
  }

  /** Playwright / calib: list live enemies. */
  debugList(): Array<{
    id: number;
    kind: EnemyKind;
    x: number;
    y: number;
    z: number;
    yaw: number;
    hp: number;
    state: AiState;
    combatMove: GeCombatMove;
    ducked: boolean;
    coverBrushId: string | null;
    alerted: boolean;
    investigateT: number;
  }> {
    return this.enemies.map((e) => ({
      id: e.id,
      kind: e.kind,
      x: e.body.position.x,
      y: e.body.position.y,
      z: e.body.position.z,
      yaw: e.yaw,
      hp: e.hp,
      state: e.state,
      combatMove: e.combatMove,
      ducked: e.ducked,
      coverBrushId: e.coverBrushId,
      alerted: e.alerted,
      investigateT: e.investigateT,
    }));
  }

  debugFreeze(id: number, frozen = true): boolean {
    const e = this.enemies.find((x) => x.id === id);
    if (!e) return false;
    e.frozen = frozen;
    e.body.velocity.set(0, 0, 0);
    e.alerted = true;
    return true;
  }

  /** Snap enemy back to spawn home for stable screenshots. */
  debugResetPose(id: number, yaw?: number): boolean {
    const e = this.enemies.find((x) => x.id === id);
    if (!e) return false;
    e.body.position.copy(e.home);
    e.body.velocity.set(0, 0, 0);
    if (yaw !== undefined) e.yaw = yaw;
    e.frozen = true;
    e.alerted = true;
    syncRoot(e);
    return true;
  }

  debugForceAim(id: number): boolean {
    const e = this.enemies.find((x) => x.id === id);
    if (!e || e.dead) return false;
    e.frozen = true;
    e.alerted = true;
    e.attackPhase = 'none';
    e.attackLockT = 0;
    e.body.velocity.set(0, 0, 0);
    if (e.kind === 'melee') {
      playClip(e, 'guard', 0.05, true);
    } else {
      playAimLoco(e, 'aim', 'idle', 0.05, true);
    }
    return true;
  }

  /** Stop clips and reset skeleton to bind (diag / screenshots). */
  debugBindPose(id: number): boolean {
    const e = this.enemies.find((x) => x.id === id);
    if (!e) return false;
    e.frozen = true;
    e.alerted = true;
    e.attackPhase = 'none';
    e.attackLockT = 0;
    e.body.velocity.set(0, 0, 0);
    e.currentClip = 'idle';
    e.upperClip = 'idle';
    e.lowerClip = 'idle';
    e.layerMode = 'full';
    applyProcEnemyPose(e, 0);
    syncRoot(e);
    return true;
  }

  debugForceShoot(id: number): boolean {
    const e = this.enemies.find((x) => x.id === id);
    if (!e || e.dead || e.kind === 'melee') return false;
    e.frozen = true;
    e.alerted = true;
    e.attackPhase = 'shoot';
    e.attackLockT = SHOOT_LOCK;
    e.body.velocity.set(0, 0, 0);
    // Aim hold + short lock — Kimodo shoot upper is unusably extreme
    playAimLoco(e, 'aim', 'idle', 0.05, true);
    return true;
  }

  debugSetWeaponSocket(
    id: number,
    pos: { x: number; y: number; z: number },
    rot: { x: number; y: number; z: number },
  ): boolean {
    const e = this.enemies.find((x) => x.id === id);
    if (!e?.weapon) return false;
    e.weapon.position.set(pos.x, pos.y, pos.z);
    e.weapon.rotation.order = 'YXZ';
    e.weapon.rotation.set(rot.x, rot.y, rot.z);
    e.weaponBasePos.copy(e.weapon.position);
    e.weaponBaseRot.set(rot.x, rot.y, rot.z, 'YXZ');
    e.weaponRecoil = 0;
    return true;
  }

  debugGetWeapon(id: number): THREE.Object3D | null {
    return this.enemies.find((x) => x.id === id)?.weapon ?? null;
  }

  debugGetRoot(id: number): THREE.Object3D | null {
    return this.enemies.find((x) => x.id === id)?.root ?? null;
  }

  async load(): Promise<void> {
    this.kit = createProcEnemyKit();
    this.ready = true;
  }

  spawnAll(spawns: EnemySpawn[] = DEFAULT_ENEMY_SPAWNS): void {
    if (!this.kit) return;
    for (const s of spawns) this.spawn(s);
  }

  spawn(spawn: EnemySpawn): void {
    if (!this.kit) return;
    const def = DEFS[spawn.kind];
    const n = this.spawnCount++;
    const variant = pickEnemyVariant(spawn.kind, n);
    const look = pickCharLook(spawn.kind, n);
    const headgear = pickGuardHeadgear(spawn.kind, n);
    const model = buildProcEnemy(variant, this.kit, spawn.kind === 'melee', spawn.kind, headgear, look);
    normalizeCharacter(model);
    const face = cloneEnemyFace(model, look);

    const root = new THREE.Group();
    root.name = `enemy_${spawn.kind}_${this.nextId}`;
    root.add(model);
    root.position.copy(spawn.position);
    root.rotation.y = spawn.yaw ?? 0;
    this.scene.add(root);

    const actions = procClipSet();
    const rightHand = findBone(model, ['RightHand']);
    const leftHand = findBone(model, ['mixamorigLeftHand', 'LeftHand', 'L_Hand', 'hand_l']);
    const leftForeArm = findBone(model, [
      'mixamorigLeftForeArm',
      'LeftForeArm',
      'LeftForearm',
      'L_ForeArm',
    ]);
    const leftArm = findBone(model, ['mixamorigLeftArm', 'LeftArm', 'L_UpperArm', 'L_Arm']);
    const rightArm = findBone(model, ['mixamorigRightArm', 'RightArm', 'R_UpperArm', 'R_Arm']);
    const rightForeArm = findBone(model, [
      'mixamorigRightForeArm',
      'RightForeArm',
      'RightForearm',
      'R_ForeArm',
    ]);
    const leftFoot = findBone(model, ['mixamorigLeftFoot', 'LeftFoot', 'L_Foot']);
    const rightFoot = findBone(model, ['mixamorigRightFoot', 'RightFoot', 'R_Foot']);
    const holdId = gunWeaponId(spawn.kind) ?? 'punch';
    const sleeveHex = this.kit.variants[variant].sleeve.color.getHex();
    const fpsHold = createFpsWorldHold(holdId, sleeveHex, look.skin);
    attachFpsWorldHold(root, fpsHold, this.nextId);
    const weapon = holdId === 'punch' ? null : fpsHold.weapon;
    const weaponBasePos = new THREE.Vector3();
    const weaponBaseRot = new THREE.Euler(0, 0, 0, 'YXZ');

    const lookHead = findBone(model, ['mixamorigHead', 'Head']);
    const lookNeck = findBone(model, ['mixamorigNeck', 'Neck']);
    const breathSpine = findBone(model, ['mixamorigSpine1', 'Spine1', 'mixamorigSpine', 'Spine']);
    const lookHeadBind = lookHead?.quaternion.clone() ?? new THREE.Quaternion();
    const lookNeckBind = lookNeck?.quaternion.clone() ?? new THREE.Quaternion();
    const body: CapsuleBody = {
      position: spawn.position.clone(),
      velocity: new THREE.Vector3(),
      radius: 0.38,
      height: ENEMY_STAND_HEIGHT,
      grounded: false,
      groundNormal: new THREE.Vector3(0, 1, 0),
    };
    if (spawn.script !== 'toiletSit') {
      const cleared = findClearSpawn(this.world, body, ENEMY_STAND_HEIGHT);
      if (cleared) {
        body.position.copy(cleared);
        root.position.copy(cleared);
      } else {
        console.warn('[EnemySystem] blocked spawn, using authored point', spawn.position);
      }
    }

    const id = this.nextId++;
    root.userData.enemyId = id;
    const hull = new THREE.Mesh(
      new THREE.CapsuleGeometry(0.36, 0.7, 1, 6),
      new THREE.MeshBasicMaterial({ visible: false }),
    );
    hull.name = 'hitHull';
    hull.position.y = 0.76;
    root.add(hull);
    model.traverse((o: THREE.Object3D) => {
      o.userData.enemyId = id;
      o.userData.enemyHit = true;
    });
    hull.userData.enemyId = id;
    hull.userData.enemyHit = true;

    const home = body.position.clone();
    const e: EnemyRuntime = {
      id,
      kind: spawn.kind,
      def,
      root,
      model,
      actions,
      currentClip: '',
      upperClip: 'idle',
      lowerClip: 'idle',
      animT: Math.random() * Math.PI * 2,
      layerMode: 'full',
      body,
      yaw: spawn.yaw ?? 0,
      hp: def.hp,
      state: 'roam',
      stateT: 0,
      attackCd: 0.8 + Math.random() * 0.8,
      attackLockT: 0,
      attackPhase: 'none',
      pendingDamage: false,
      punchId: 'jabR',
      punchComboStep: 0,
      hurtT: 0,
      hurtCooldown: 0,
      alerted: false,
      dead: false,
      home,
      roamTarget: home.clone(),
      roamWaitT: 0.5 + Math.random(),
      roamFidget: Math.random() < 0.5,
      strafeSign: Math.random() < 0.5 ? -1 : 1,
      strafeT: 1.4 + Math.random() * 1.2,
      lookHead,
      lookNeck,
      lookHeadBind,
      lookNeckBind,
      lookYaw: 0,
      lookPitch: 0,
      leftHand,
      leftForeArm,
      leftArm,
      rightArm,
      rightForeArm,
      leftFoot,
      rightFoot,
      rightHand,
      frozen: false,
      weapon,
      fpsHold,
      fpsAds: 0,
      adsHoldT: 0,
      weaponBasePos,
      weaponBaseRot,
      weaponRecoil: 0,
      burstLeft: 0,
      burstPauseT: 0,
      duckT: 0,
      combatDecisionT: 0.4 + Math.random() * 0.8,
      combatMove: 'none',
      combatMoveT: 0,
      geActionBusyT: 0,
      lookaroundT: 0,
      investigateT: 0,
      heardPos: home.clone(),
      heardGunshot: false,
      lastKnown: home.clone(),
      nav: createNavFollow(),
      ducked: false,
      duckSink: 0,
      kneelBlend: 0,
      standHeight: ENEMY_STAND_HEIGHT,
      duckHeight: ENEMY_DUCK_HEIGHT,
      modelBaseY: model.position.y,
      coverWanted: false,
      coverBrushId: null,
      coverPos: new THREE.Vector3(),
      coverHidePos: new THREE.Vector3(),
      peekPos: new THREE.Vector3(),
      coverT: 0,
      coverIsShort: false,
      coverCooldownT: 0,
      coverCheckPos: new THREE.Vector3(),
      coverCheckT: 0,
      ragdoll: null,
      bloodMarks: [],
      hurtOverlay: null,
      aimLocoWalk: false,
      breathT: Math.random() * Math.PI * 2,
      breathSpine,
      spineLean: 0,
      variant,
      face,
      lostSightT: 0,
      talkT: 0,
      talkMouthT: 0,
      barkCd: 0,
      pointT: 0,
      pendingLookaround: false,
      lastBark: '',
      stopTalk: null,
      combatAge: 0,
      fleeing: false,
      brokeAndRan: false,
      hurtBarkCd: 0,
      handsUpSaid: false,
      fistsTauntSaid: false,
      sitBlend: spawn.script === 'toiletSit' ? 1 : 0,
      toiletPhase: spawn.script === 'toiletSit' ? 'sit' : 'none',
      toiletT: 0,
      toiletBayX: spawn.script === 'toiletSit' ? spawn.position.x : 0,
      toiletDoorId: spawn.toiletDoorId ?? '',
      headgear,
      hat: headgear === 'hat' ? findNamed(model, 'guard_hat') : null,
      helmet: headgear === 'helmet' ? findNamed(model, 'guard_helmet') : null,
      headgearRemoved: false,
      quietGearKnock: false,
      alarmRun: false,
    };
    if (isFriendlyKind(spawn.kind)) setEnemyFaceMood(face, 'scared');
    pickRoamTarget(e);
    playClip(e, 'idle', FADE_LOCO, true);
    applyProcEnemyPose(e, 0);
    this.enemies.push(e);
  }

  applyHit(
    object: THREE.Object3D | null,
    damage: number,
    point?: THREE.Vector3,
    dir?: THREE.Vector3,
    weaponId?: WeaponId,
    meleeStun?: number,
  ): false | 'hit' | 'blocked' {
    if (!object) return false;
    let o: THREE.Object3D | null = object;
    let id: number | undefined;
    while (o) {
      if (typeof o.userData?.enemyId === 'number') {
        id = o.userData.enemyId as number;
        break;
      }
      o = o.parent;
    }
    if (id === undefined) return false;
    const e = this.enemies.find((x) => x.id === id);
    if (!e) return false;

    if (weaponId === 'punch' && this.tryMeleeBlock(e)) {
      return 'blocked';
    }

    const gear = headgearFromHit(object);
    const zone = point ? hitZoneFromPoint(e, point) : 'torso';
    if (e.headgear === 'hat' && e.hat && (gear === 'hat' || zone === 'head')) {
      this.knockHeadgearOff(e, e.hat, 'hat', dir, point);
      this.cutVoice(e);
      if (!e.alerted) e.quietGearKnock = true;
      else this.reactToHeadgearKnock(e);
      return 'hit';
    }

    if (e.headgear === 'helmet' && e.helmet && (gear === 'helmet' || zone === 'head')) {
      this.knockHeadgearOff(e, e.helmet, 'helmet', dir, point);
      this.cutVoice(e);
      this.reactToHeadgearKnock(e);
      return 'hit';
    }
    if (zone === 'head' && e.headgear === 'none' && e.headgearRemoved) {
      damage = Math.max(damage, e.hp);
    }
    spawnBloodMark(e, point, zone);

    if (e.dead) {
      if (e.ragdoll && !e.ragdoll.sleeping && dir) {
        const flat = dir.clone();
        flat.y = 0;
        if (flat.lengthSq() > 1e-8) flat.normalize();
        addRagdollImpulse(
          e.ragdoll,
          flat,
          point,
          weaponId === 'shotgun' || weaponId === 'sawedoff' ? 0.35 : 0.1,
        );
      }
      return 'hit';
    }

    e.hp -= damage;
    const punched = weaponId === 'punch';
    this.cutVoice(e);
    this.playHurtVoice(e, punched);
    const wasAlerted = e.alerted;
    if (isFriendlyKind(e.kind)) {
      if (e.hp > 0) this.beginFlee(e, 'protest');
    } else {
      this.aggroOnAttacker(e);
      if (!wasAlerted && e.hp > 0) this.tryAlarmRun(e, 0.22);
    }
    this.alertNearHurt(e);

    const shotDir = dir?.clone() ?? (point
      ? this.tmp.set(point.x - e.root.position.x, 0, point.z - e.root.position.z).normalize()
      : new THREE.Vector3(0, 0, -1));
    shotDir.y = 0;
    if (shotDir.lengthSq() < 1e-6) shotDir.set(0, 0, -1);
    else shotDir.normalize();

    if (e.hp <= 0) {
      e.hp = 0;
      e.dead = true;
      e.state = 'dead';
      e.attackPhase = 'none';
      hideEnemyFpsHold(e);
      e.pendingDamage = false;
      e.hurtOverlay = null;
      e.body.velocity.set(0, 0, 0);
      e.ducked = false;
      e.body.height = e.standHeight;
      e.model.position.y = e.modelBaseY;
      clearCover(e);
      e.root.visible = true;
      e.model.visible = true;
      const floorY = e.body.position.y;
      e.ragdoll = createRagdoll(
        e.model,
        weaponId === 'shotgun' || weaponId === 'sawedoff' ? 1.55 : 1.1,
        floorY,
      );
      if (e.ragdoll) {
        addRagdollImpulse(e.ragdoll, shotDir, point, knockbackStrength(weaponId));
        writeRagdollBones(e.ragdoll);
      } else {
        collapseEnemyVisual(e.model, shotDir, floorY);
      }
      e.currentClip = '';
      if (!isFriendlyKind(e.kind)) this.onHostileKilled?.(e.body.position);
    } else if (e.fleeing) {
      // Stay in flee — don't snap back to hurt/cover.
    } else if (e.alarmRun) {
      // Keep sprinting for the button.
    } else if (!isFriendlyKind(e.kind) && this.tryStartCombatFlee(e)) {
      // Mortally wounded gunner broke and ran.
    } else if (punched || e.hurtCooldown <= 0) {
      const stun = punched ? (meleeStun ?? 0.45) : hurtDurationFor(e, zone);
      e.hurtT = punched ? Math.max(e.hurtT, stun) : stun;
      e.hurtCooldown = punched ? 0.2 : 0.6;
      e.state = 'hurt';
      e.stateT = 0;
      e.attackPhase = 'none';
      e.pendingDamage = false;
      e.attackLockT = e.hurtT;
      if (punched) {
        const knock = stun * 3.2;
        e.body.velocity.x += shotDir.x * knock;
        e.body.velocity.z += shotDir.z * knock;
      }
      if (e.kind !== 'melee') {
        e.coverWanted = true;
        e.coverCooldownT = 0;
      }
      playHurtReaction(e, punched ? 'torso' : zone);
    }
    return 'hit';
  }

  private knockHeadgearOff(
    e: EnemyRuntime,
    mesh: THREE.Object3D,
    kind: 'hat' | 'helmet',
    dir?: THREE.Vector3,
    point?: THREE.Vector3,
  ): void {
    mesh.updateWorldMatrix(true, true);
    const pos = mesh.getWorldPosition(new THREE.Vector3());
    const quat = mesh.getWorldQuaternion(new THREE.Quaternion());
    mesh.removeFromParent();
    mesh.position.copy(pos);
    mesh.quaternion.copy(quat);
    mesh.traverse((o) => {
      delete o.userData.enemyId;
      delete o.userData.enemyHit;
      o.userData.ignoreHit = true;
    });
    this.scene.add(mesh);
    const kick = (dir ?? new THREE.Vector3(0, 0, -1)).clone();
    kick.y = 0;
    if (kick.lengthSq() < 1e-6) kick.set(Math.random() - 0.5, 0, Math.random() - 0.5);
    kick.normalize();
    const speed = kind === 'helmet' ? 3.4 : 4.2;
    const lift = kind === 'helmet' ? 2.8 : 3.4;
    this.looseHats.push({
      mesh,
      vel: new THREE.Vector3(kick.x * speed, lift, kick.z * speed),
      spin: new THREE.Vector3((Math.random() - 0.5) * 8, (Math.random() - 0.5) * 10, (Math.random() - 0.5) * 8),
      life: 4,
    });
    if (kind === 'hat') e.hat = null;
    else e.helmet = null;
    e.headgear = 'none';
    e.headgearRemoved = true;
    this.audio.playAt(kind === 'helmet' ? 'helmetHit' : 'switchClick', point ?? e.root.position, this.listenerPos, {
      volume: kind === 'helmet' ? 0.7 : 0.62,
      rate: kind === 'helmet' ? 0.82 + Math.random() * 0.14 : 0.72 + Math.random() * 0.1,
      range: 32,
    });
  }

  private updateLooseHats(dt: number): void {
    for (let i = this.looseHats.length - 1; i >= 0; i--) {
      const h = this.looseHats[i]!;
      h.life -= dt;
      h.vel.y -= 18 * dt;
      h.mesh.position.addScaledVector(h.vel, dt);
      h.mesh.rotation.x += h.spin.x * dt;
      h.mesh.rotation.y += h.spin.y * dt;
      h.mesh.rotation.z += h.spin.z * dt;
      let floor = -Infinity;
      for (const b of this.world.brushes) {
        const y = brushHeightAt(b, h.mesh.position.x, h.mesh.position.z);
        if (y > floor && y <= h.mesh.position.y + 0.35) floor = y;
      }
      if (floor > -Infinity && h.mesh.position.y <= floor + 0.04) {
        h.mesh.position.y = floor + 0.04;
        h.vel.set(0, 0, 0);
        h.spin.multiplyScalar(0.2);
      }
      if (h.life <= 0) this.looseHats.splice(i, 1);
    }
  }

  /** Unarmed punchers can catch a fist. Gunners never block. */
  private tryMeleeBlock(e: EnemyRuntime): boolean {
    if (e.kind !== 'melee' || e.dead || e.fleeing || e.state === 'hurt') return false;
    let chance = 0.36;
    if (e.attackPhase === 'swing') chance = 0.12;
    else if (e.attackPhase === 'windup') chance = 0.22;
    if (Math.random() > chance) return false;
    e.attackPhase = 'none';
    e.pendingDamage = false;
    e.attackLockT = 0.22;
    e.state = 'attack';
    playClip(e, 'guard', FADE_COMBAT, true);
    this.audio.playAt(e.def.hurtSfx, e.root.position, this.listenerPos, {
      volume: 0.22,
      rate: 1.35 + Math.random() * 0.2,
      range: 36,
    });
    return true;
  }

  /** Radius splash for rockets / grenades. Returns true if anything was hit. */
  applySplash(
    origin: THREE.Vector3,
    radius: number,
    maxDamage: number,
    weaponId?: WeaponId,
  ): boolean {
    let hit = false;
    for (const e of this.enemies) {
      if (e.dead && !e.ragdoll) continue;
      const dx = e.body.position.x - origin.x;
      const dy = e.body.position.y + 0.9 - origin.y;
      const dz = e.body.position.z - origin.z;
      const dist = Math.hypot(dx, dy, dz);
      if (dist > radius) continue;
      const falloff = 1 - dist / radius;
      const dmg = Math.max(1, Math.round(maxDamage * falloff));
      const dir = this.tmp3.set(dx, 0, dz);
      if (dir.lengthSq() > 1e-6) dir.normalize();
      else dir.set(0, 0, -1);
      this.applyHit(e.root, dmg, origin, dir, weaponId);
      hit = true;
    }
    return hit;
  }

  update(player: PlayerController, dt: number, playerFistsUp = false): void {
    this.updateLooseHats(dt);
    this.nav.beginFrame();
    this.playerFistsUp = playerFistsUp;
    const ppos = player.body.position;
    const eye = player.camera.position;
    this.playerKnown.copy(ppos);
    this.listenerPos.copy(eye);
    for (const e of this.enemies) {
      e.stateT += dt;
      e.attackCd = Math.max(0, e.attackCd - dt);
      e.attackLockT = Math.max(0, e.attackLockT - dt);
      e.adsHoldT = Math.max(0, e.adsHoldT - dt);
      e.hurtCooldown = Math.max(0, e.hurtCooldown - dt);
      e.hurtBarkCd = Math.max(0, e.hurtBarkCd - dt);
      applyWeaponRecoil(e, dt);

      if (e.dead) {
        e.root.visible = true;
        e.model.visible = true;
        if (e.ragdoll && !e.ragdoll.sleeping) {
          stepRagdollPhysics(e.ragdoll, dt);
          writeRagdollBones(e.ragdoll);
          const hips = ragdollHips(e.ragdoll);
          e.body.position.set(hips.x, e.ragdoll.floorY, hips.z);
          e.body.velocity.set(0, 0, 0);
        }
        fadeBlood(e, dt);
        continue;
      }

      e.duckSink += ((e.ducked ? ENEMY_DUCK_SINK : 0) - e.duckSink) * (1 - Math.exp(-7.2 * dt));
      const show = this.shows(e);
      if (show) {
        applyProcEnemyPose(e, dt);
        applyHurtOverlay(e, dt);
      }
      fadeBlood(e, dt);
      if (e.toiletPhase !== 'none') {
        this.updateToiletSitter(e, player, dt);
        this.tickTalk(e, dt);
        if (show) tickEnemyBlink(e.face, dt);
        syncRoot(e);
        if (e.toiletPhase === 'noise' || e.toiletPhase === 'rise' || e.toiletPhase === 'leave') {
          this.tmp.set(e.heardPos.x, e.heardPos.y + 1.45, e.heardPos.z);
          applyLookAt(e, this.tmp, dt);
        } else if (e.toiletPhase !== 'sit') {
          applyLookAt(e, eye, dt);
        }
        continue;
      }
      applyCombatLife(e, dt);
      this.tickTalk(e, dt);
      if (show) tickEnemyBlink(e.face, dt);

      // Melee damage at the authored punch impact (pos2), same clips as the FPS fists.
      if (e.attackPhase === 'swing' && e.pendingDamage) {
        const clip = bakedPunchClip(e.punchId);
        const dur = punchPlayDuration(clip);
        const u = 1 - Math.min(1, e.attackLockT / dur);
        if (u >= punchImpactT(clip)) {
          e.pendingDamage = false;
          if (horizontalDist(e.body.position, ppos) <= e.def.attackRange + 0.35) {
            player.takeDamage(e.def.damage, e.body.position);
            this.audio.play('hitBody', { volume: 0.58, rate: 0.94 + Math.random() * 0.12 });
          }
        }
      }

      if (e.attackPhase !== 'none' && e.attackLockT <= 0) {
        if (e.attackPhase === 'windup') {
          e.attackPhase = 'swing';
          e.attackLockT = punchPlayDuration(bakedPunchClip(e.punchId));
          e.pendingDamage = true;
          playClip(e, 'melee', FADE_HURT, false);
          this.audio.playAt('meleeSwing', e.root.position, this.listenerPos, {
            volume: 0.42,
            rate: punchSwingRate(e.punchId) + (Math.random() - 0.5) * 0.04,
            range: 28,
          });
        } else if (e.attackPhase === 'aim') {
          this.doGunAttack(e, player);
          this.finishGunShot(e);
        } else {
          // Leave one-shot end pose immediately — clamped LoopOnce can stick in bind/T-pose.
          e.attackPhase = 'none';
          e.pendingDamage = false;
          if (e.kind === 'melee') {
            e.state = 'chase';
            playClip(e, 'guard', FADE_COMBAT, true);
          } else if (
            e.state !== 'peek' &&
            e.state !== 'cover' &&
            e.state !== 'duck' &&
            e.state !== 'seekCover'
          ) {
            e.state = 'attack';
            playAimLoco(e, 'aim', aimLower(e), FADE_COMBAT, true);
          }
        }
      }

      if (e.state === 'hurt') {
        e.hurtT -= dt;
        e.body.velocity.x *= 0.85;
        e.body.velocity.z *= 0.85;
        this.world.move(e.body, dt);
        syncRoot(e);
        if (e.hurtT <= 0 || hurtClipDone(e)) {
          e.hurtT = 0;
          e.stateT = 0;
          if (e.fleeing) {
            e.state = 'flee';
            e.attackLockT = 0;
            restoreHurtLoco(e);
          } else if (e.kind !== 'melee' && e.coverWanted) {
            // Resolve a slot immediately; fall back to open fight if none
            e.state = 'seekCover';
            e.coverBrushId = null;
          } else {
            e.state = e.kind === 'melee' ? 'chase' : e.alerted ? 'attack' : 'chase';
          }
          e.attackLockT = 0;
          restoreHurtLoco(e);
        }
        continue;
      }

      // Melee swing lock — face player, minimal movement.
      // Gunners must keep running the combat brain during shoot lock so
      // burst pauses, duck, and cover seek timers actually advance.
      if (e.kind === 'melee' && e.attackLockT > 0 && e.attackPhase !== 'none') {
        this.tmp.subVectors(ppos, e.body.position);
        this.tmp.y = 0;
        if (this.tmp.lengthSq() > 1e-6) {
          this.tmp.normalize();
          e.yaw = Math.atan2(-this.tmp.x, -this.tmp.z);
        }
        e.body.velocity.x *= 0.7;
        e.body.velocity.z *= 0.7;
        this.world.move(e.body, dt);
        syncRoot(e);
        applyLookAt(e, eye, dt);
        continue;
      }

      if (e.frozen) {
        e.body.velocity.set(0, 0, 0);
        this.tmp.subVectors(ppos, e.body.position);
        this.tmp.y = 0;
        if (this.tmp.lengthSq() > 1e-6) {
          this.tmp.normalize();
          e.yaw = Math.atan2(-this.tmp.x, -this.tmp.z);
        }
        syncRoot(e);
        applySupportHandClasp(e);
        applyLookAt(e, eye, dt);
        continue;
      }

      this.tmp.subVectors(ppos, e.body.position);
      this.tmp.y = 0;
      const dist = this.tmp.length();
      const toPlayer = this.tmp2.copy(this.tmp);
      if (dist > 1e-4) toPlayer.multiplyScalar(1 / dist);

      if (e.fleeing || e.state === 'flee') {
        this.updateFlee(e, player, toPlayer, dist, dt);
        continue;
      }
      if (e.alarmRun || e.state === 'alarm') {
        this.updateAlarmRun(e, dt);
        continue;
      }
      if (isFriendlyKind(e.kind)) {
        if (e.investigateT > 0) this.updateScaredHear(e, dt);
        else this.updateFriendly(e, player, toPlayer, dist, dt);
        continue;
      }

      if (!e.alerted) {
        const los = hasLineOfSight(this.ray, this.world, e.body.position, ppos, eyeHeightFor(e));
        if (canSeePlayer(e, toPlayer, dist, los, player.crouchBlend > 0.5)) {
          this.becomeAlerted(e, eye);
        } else {
          const hearR = footstepHearRange(player);
          if (hearR > 0 && hearDist(e.body.position, ppos) < hearR && this.soundCarries(ppos, e.body.position, 1)) {
            this.beginInvestigate(e, ppos, false);
          }
          if (e.investigateT > 0) this.updateInvestigate(e, dt);
          else this.updateRoam(e, dt);
          continue;
        }
      }

      const losNow = hasLineOfSight(this.ray, this.world, e.body.position, ppos, eyeHeightFor(e));
      const seeing = canSeePlayer(e, toPlayer, dist, losNow, player.crouchBlend > 0.5);
      const hearR = footstepHearRange(player);
      const heard =
        hearR > 0 && hearDist(e.body.position, ppos) < hearR && this.soundCarries(ppos, e.body.position, 1);
      if (seeing && this.isComeOutPlaying(e)) this.barkSpotted(e);
      if (seeing || heard) {
        e.lostSightT = 0;
        e.lastKnown.copy(ppos);
        e.combatAge += dt;
        if (seeing && this.playerFistsUp && !e.fistsTauntSaid && e.barkCd <= 0) {
          this.barkFistsTaunt(e);
        } else if (seeing && e.combatAge > TAUNT_AFTER && e.barkCd <= 0 && Math.random() < 0.12) {
          if (this.playerFistsUp && Math.random() < 0.55) this.barkFistsTaunt(e);
          else this.bark(e, eye);
        } else if (!seeing && e.barkCd <= 0 && e.combatAge > 0.7 && Math.random() < 0.1) {
          this.barkHunt(e);
        }
      } else {
        e.lostSightT += dt;
        if (e.lostSightT >= LOSE_SIGHT_TIME) {
          if (this.alarms?.sounded) {
            e.lostSightT = 0;
          } else {
            this.calmDown(e);
            this.updateRoam(e, dt);
            continue;
          }
        }
        if (e.barkCd <= 0 && e.lostSightT > 0.55 && Math.random() < 0.1) this.barkHunt(e);
      }

      if (e.pointT > 0) {
        e.pointT -= dt;
        e.yaw = Math.atan2(-toPlayer.x, -toPlayer.z);
        e.body.velocity.x *= 0.45;
        e.body.velocity.z *= 0.45;
        this.world.move(e.body, dt);
        syncRoot(e);
        playClip(e, 'point', FADE_LOCO, true);
        applyLookAt(e, eye, dt);
        if (e.pointT <= 0) {
          if (e.pendingLookaround && e.actions.has('lookaround')) {
            e.lookaroundT = 0.7 + Math.random() * 1.4;
            e.state = 'roam';
            e.body.velocity.set(0, 0, 0);
            playClip(e, 'lookaround', FADE_LOCO, false);
          } else {
            e.state = 'chase';
          }
          e.pendingLookaround = false;
        }
        continue;
      }

      if (e.lookaroundT > 0) {
        e.lookaroundT -= dt;
        e.body.velocity.x *= 0.5;
        e.body.velocity.z *= 0.5;
        this.world.move(e.body, dt);
        syncRoot(e);
        applyLookAt(e, eye, dt);
        if (e.lookaroundT <= 0) e.state = 'chase';
        continue;
      }

      if (!seeing && !heard) {
        e.state = 'chase';
        this.tmp.set(e.lastKnown.x - e.body.position.x, 0, e.lastKnown.z - e.body.position.z);
        if (this.tmp.lengthSq() > 1e-6) {
          this.tmp.normalize();
          e.yaw = Math.atan2(-this.tmp.x, -this.tmp.z);
        }
        const seekD = horizontalDist(e.body.position, e.lastKnown);
        const speed = seekD > 6 ? e.def.runSpeed : e.def.walkSpeed;
        this.steerTo(e, e.lastKnown, speed, dt);
        if (e.kind === 'melee') playMeleeLoco(e, speed > 3.5 ? 'run' : 'walk', FADE_LOCO);
        else playClip(e, speed > 3.5 ? 'run' : 'walk', FADE_LOCO, true);
        this.world.move(e.body, dt);
        syncRoot(e);
        applySupportHandClasp(e);
        this.tmp.set(e.lastKnown.x, e.lastKnown.y + 1.45, e.lastKnown.z);
        applyLookAt(e, this.tmp, dt);
        continue;
      }

      e.yaw = Math.atan2(-toPlayer.x, -toPlayer.z);
      const inAttack = dist <= e.def.attackRange;
      const los = losNow;

      if (e.kind === 'melee') {
        if (inAttack && e.attackCd <= 0 && los) {
          this.startMeleeAttack(e);
        } else if (dist > e.def.preferRange) {
          e.state = 'chase';
          const speed = dist > 6 ? e.def.runSpeed : e.def.walkSpeed;
          this.chaseToward(e, ppos, toPlayer, speed, los, dt);
          playMeleeLoco(e, speed > 3.5 ? 'run' : 'walk', FADE_LOCO);
        } else {
          e.state = 'attack';
          e.body.velocity.x *= 0.5;
          e.body.velocity.z *= 0.5;
          playClip(e, 'guard', FADE_COMBAT, true);
        }
      } else {
        this.updateGunnerCombat(e, player, toPlayer, dist, los, dt);
      }

      this.world.move(e.body, dt);
      syncRoot(e);
      applySupportHandClasp(e);
      applyLookAt(e, eye, dt);
    }

    for (const e of this.enemies) {
      if (this.shows(e)) updateEnemyFpsHold(e, dt);
    }

    for (const e of this.enemies) {
      if (e.dead || e.sitBlend > 0.7) continue;
      const spd = e.body.velocity.x * e.body.velocity.x + e.body.velocity.z * e.body.velocity.z;
      if (spd < 0.35) continue;
      this.doors?.openNear(e.body.position);
    }
  }

  /**
   * GoldenEye gunner brain (n64decomp/007 global AI lists):
   * chase → 0006 Take-a-shot moves (dodge / charge / stand|kneel) → cover hide/peek when pressed.
   */
  private updateGunnerCombat(
    e: EnemyRuntime,
    player: PlayerController,
    toPlayer: THREE.Vector3,
    dist: number,
    los: boolean,
    dt: number,
  ): void {
    e.burstPauseT = Math.max(0, e.burstPauseT - dt);
    e.combatDecisionT = Math.max(0, e.combatDecisionT - dt);
    e.coverCooldownT = Math.max(0, e.coverCooldownT - dt);
    e.geActionBusyT = Math.max(0, e.geActionBusyT - dt);

    // Flanked / too close while using cover — abandon and fight open
    if (
      (e.state === 'cover' || e.state === 'peek' || e.state === 'seekCover') &&
      dist < COVER_ABANDON_DIST
    ) {
      clearCover(e);
      e.coverCooldownT = 2.0 + Math.random() * 1.2;
      setEnemyCrouch(e, this.world, false);
      e.state = 'attack';
      e.stateT = 0;
      e.combatMove = 'none';
      e.combatMoveT = 0;
    }

    if (e.state === 'seekCover') {
      if (e.stateT > 6.5) {
        clearCover(e);
        e.coverCooldownT = 2.5 + Math.random() * 1.5;
        setEnemyCrouch(e, this.world, false);
        e.state = 'attack';
        e.stateT = 0;
        e.combatDecisionT = 0.2;
        e.combatMove = 'none';
      } else {
        this.updateSeekCover(e, player.body.position, toPlayer, dist, dt);
      }
      return;
    }
    if (e.state === 'cover') {
      this.updateInCover(e, player, toPlayer, dist, los, dt);
      return;
    }
    if (e.state === 'peek') {
      this.updatePeek(e, player, toPlayer, dist, los, dt);
      return;
    }
    if (e.state === 'duck') {
      this.updateDuck(e, player, toPlayer, dist, los, dt);
      return;
    }

    // Too far: jog toward Bond (GE 0008), with occasional walk/run & fire (0006 group 3)
    if (dist > e.def.preferRange + 0.8) {
      if (
        e.coverCooldownT <= 0 &&
        (e.coverWanted || (!los && dist < e.def.attackRange))
      ) {
        if (this.tryBeginSeekCover(e, player.body.position)) return;
      }
      // Continue / start a GE charge when Bond is >10m
      if (
        e.combatMove === 'advanceWalk' ||
        e.combatMove === 'advanceRun' ||
        (e.combatMoveT > 0 &&
          (e.combatMove === 'sidestep' || e.combatMove === 'runSide'))
      ) {
        this.updateOpenFight(e, player, toPlayer, dist, los, dt);
        return;
      }
      if (
        los &&
        dist > GE_CHARGE_DIST &&
        e.geActionBusyT <= 0 &&
        e.combatDecisionT <= 0
      ) {
        // Mirror 0006: ~25% walk&fire, ~25% run&fire, else keep jogging
        const r = Math.random() * 255;
        if (r <= 64) {
          e.combatMove = 'advanceWalk';
          e.combatMoveT = 1.6 + Math.random() * 0.8;
          e.geActionBusyT = GE_ACTION_BUSY;
          this.updateOpenFight(e, player, toPlayer, dist, los, dt);
          return;
        }
        if (r <= 127) {
          e.combatMove = 'advanceRun';
          e.combatMoveT = 1.4 + Math.random() * 0.7;
          e.geActionBusyT = GE_ACTION_BUSY;
          this.updateOpenFight(e, player, toPlayer, dist, los, dt);
          return;
        }
        e.combatDecisionT = 0.7 + Math.random() * 0.6;
      }
      e.state = 'chase';
      e.combatMove = 'none';
      setEnemyCrouch(e, this.world, false);
      const speed = dist > e.def.preferRange + 4 ? e.def.runSpeed : e.def.walkSpeed;
      this.chaseToward(e, los ? player.body.position : e.lastKnown, toPlayer, speed, los, dt);
      playClip(e, speed > 3.5 ? 'run' : 'walk', FADE_LOCO, true);
      if (los && dist < e.def.attackRange) this.tryBurstFire(e, player, dist, los);
      holdAimMove(e);
      return;
    }

    if (dist < e.def.preferRange - 1.2) {
      // Too close — backpedal then re-pick a GE move
      e.state = 'attack';
      e.combatMove = 'none';
      e.combatMoveT = 0;
      setEnemyCrouch(e, this.world, false);
      const speed = e.def.walkSpeed * 0.85;
      e.body.velocity.x = -toPlayer.x * speed;
      e.body.velocity.z = -toPlayer.z * speed;
      playAimLoco(e, 'aim', aimLower(e), FADE_COMBAT, true);
      this.tryBurstFire(e, player, dist, los);
      holdAimMove(e);
      if (e.combatDecisionT <= 0) e.combatDecisionT = 0.35;
      return;
    }

    this.updateOpenFight(e, player, toPlayer, dist, los, dt);
  }

  /**
   * Execute / pick GE global 0006 “Take a shot” style moves.
   * Cascade: dodge → charge (>10m) → stand|kneel shoot (50/50).
   */
  private updateOpenFight(
    e: EnemyRuntime,
    player: PlayerController,
    toPlayer: THREE.Vector3,
    dist: number,
    los: boolean,
    dt: number,
  ): void {
    e.state = 'attack';

    if (e.combatMoveT <= 0 || e.combatMove === 'none') {
      // Under fire — peel to cover before re-rolling attacks (GE “jog when shot”)
      if (e.coverWanted && e.coverCooldownT <= 0) {
        if (this.tryBeginSeekCover(e, player.body.position)) return;
      }
      this.pickGeAttack(e, toPlayer, dist);
    }

    e.combatMoveT = Math.max(0, e.combatMoveT - dt);
    const move = e.combatMove;

    if (move === 'kneelShoot') {
      // Hand off to duck state for capsule crouch + kneel clip
      e.state = 'duck';
      e.duckT = Math.max(e.combatMoveT, 0.55);
      e.combatMoveT = 0;
      setEnemyCrouch(e, this.world, true);
      e.body.velocity.x *= 0.3;
      e.body.velocity.z *= 0.3;
      playCombatClip(e, 'kneel', 'aim', FADE_COMBAT, true);
      this.tryBurstFire(e, player, dist, los);
      return;
    }

    if (move === 'hop') {
      setEnemyCrouch(e, this.world, false);
      const sx = -toPlayer.z * e.strafeSign;
      const sz = toPlayer.x * e.strafeSign;
      e.body.velocity.x = sx * RUN_SIDE_SPEED * 1.15;
      e.body.velocity.z = sz * RUN_SIDE_SPEED * 1.15;
      playCombatClip(e, 'hop', 'aim', FADE_COMBAT, false);
      this.tryBurstFire(e, player, dist, los);
    } else if (move === 'sidestep' || move === 'runSide') {
      setEnemyCrouch(e, this.world, false);
      const speed = move === 'runSide' ? RUN_SIDE_SPEED : SIDESTEP_SPEED;
      const sx = -toPlayer.z * e.strafeSign;
      const sz = toPlayer.x * e.strafeSign;
      e.body.velocity.x = sx * speed;
      e.body.velocity.z = sz * speed;
      if (e.actions.has('sidestep')) {
        playClip(e, 'sidestep', FADE_COMBAT, true);
      } else {
        playAimLoco(e, 'aim', 'walk', FADE_COMBAT, true);
      }
      this.tryBurstFire(e, player, dist, los);
    } else if (move === 'advanceWalk' || move === 'advanceRun') {
      setEnemyCrouch(e, this.world, false);
      const speed = move === 'advanceRun' ? e.def.runSpeed : e.def.walkSpeed;
      e.body.velocity.x = toPlayer.x * speed;
      e.body.velocity.z = toPlayer.z * speed;
      playAimLoco(e, 'aim', 'walk', FADE_COMBAT, true);
      this.tryBurstFire(e, player, dist, los);
    } else {
      // standShoot — plant feet, one deliberate shot
      setEnemyCrouch(e, this.world, false);
      e.body.velocity.x *= 0.35;
      e.body.velocity.z *= 0.35;
      playAimLoco(e, 'aim', 'idle', FADE_COMBAT, true);
      this.tryBurstFire(e, player, dist, los);
    }

    if (e.combatMoveT <= 0) {
      e.combatMove = 'none';
      e.combatDecisionT = 0.15 + Math.random() * 0.35;
    }
    holdAimMove(e);
  }

  /**
   * Port of GE global 0006 probability cascade (byte checks are 0–255 style).
   * @see https://ryandwyer.gitlab.io/gepd-function-explorer/ge-global-0006.html
   * @see https://archive.speedrunwiki.com/Guards
   */
  private pickGeAttack(e: EnemyRuntime, toPlayer: THREE.Vector3, dist: number): void {
    const rnd = () => Math.random() * 255;
    const trySide = (meters: number): boolean => {
      const sign = Math.random() < 0.5 ? -1 : 1;
      if (lateralSpaceClear(this.world, e.body.position, toPlayer, sign, meters)) {
        e.strafeSign = sign;
        return true;
      }
      if (lateralSpaceClear(this.world, e.body.position, toPlayer, -sign, meters)) {
        e.strafeSign = -sign;
        return true;
      }
      return false;
    };

    // Hop/dodge — shortened for ~3–4m Facility halls
    if (rnd() <= 10 && trySide(1.4)) {
      e.combatMove = e.actions.has('hop') ? 'hop' : 'sidestep';
      e.combatMoveT = 0.85 + Math.random() * 0.35;
      return;
    }
    if (rnd() <= 10 && trySide(1.4)) {
      e.combatMove = e.actions.has('hop') ? 'hop' : 'sidestep';
      e.combatMoveT = 1.0 + Math.random() * 0.4;
      return;
    }
    // Sidestep (~0.7m)
    if (rnd() <= 25 && trySide(0.7)) {
      e.combatMove = 'sidestep';
      e.combatMoveT = 0.7 + Math.random() * 0.35;
      return;
    }
    // Run sideways (1.2–2.2m), needs 3s busy clear
    if (e.geActionBusyT <= 0 && rnd() <= 50) {
      const meters = 1.2 + Math.random() * 1.0;
      if (trySide(meters)) {
        e.combatMove = 'runSide';
        e.combatMoveT = 1.4 + Math.random() * 0.7;
        e.geActionBusyT = GE_ACTION_BUSY;
        return;
      }
    }
    // Charge only if Bond > 10m and not busy (GE group 3)
    if (e.geActionBusyT <= 0 && dist > GE_CHARGE_DIST) {
      if (rnd() <= 64) {
        e.combatMove = 'advanceWalk';
        e.combatMoveT = 1.6 + Math.random() * 0.8;
        e.geActionBusyT = GE_ACTION_BUSY;
        return;
      }
      if (rnd() <= 127) {
        e.combatMove = 'advanceRun';
        e.combatMoveT = 1.4 + Math.random() * 0.7;
        e.geActionBusyT = GE_ACTION_BUSY;
        return;
      }
    }
    // Group 4 — always succeeds: 50% stand shoot / 50% kneel shoot
    if (rnd() <= 127) {
      e.combatMove = 'standShoot';
      e.combatMoveT = 1.0 + Math.random() * 0.7;
    } else {
      e.combatMove = 'kneelShoot';
      e.combatMoveT = 0.9 + Math.random() * 0.7;
    }
  }

  private updateDuck(
    e: EnemyRuntime,
    player: PlayerController,
    _toPlayer: THREE.Vector3,
    dist: number,
    los: boolean,
    dt: number,
  ): void {
    e.duckT -= dt;
    e.combatMove = 'kneelShoot';
    setEnemyCrouch(e, this.world, true);
    e.body.velocity.x *= 0.45;
    e.body.velocity.z *= 0.45;
    playCombatClip(e, 'kneel', 'aim', FADE_COMBAT, true);
    this.tryBurstFire(e, player, dist, los);

    if (e.duckT <= 0 && e.attackPhase !== 'aim' && e.attackPhase !== 'shoot') {
      setEnemyCrouch(e, this.world, false);
      e.state = 'attack';
      e.combatMove = 'none';
      e.combatMoveT = 0;
      e.combatDecisionT = 0.2 + Math.random() * 0.3;
    }
  }

  private updateSeekCover(
    e: EnemyRuntime,
    playerPos: THREE.Vector3,
    _toPlayer: THREE.Vector3,
    dist: number,
    dt: number,
  ): void {
    if (!e.coverBrushId) {
      if (!this.tryBeginSeekCover(e, playerPos)) {
        e.coverWanted = false;
        e.coverCooldownT = 2.0;
        e.state = 'attack';
        return;
      }
    }

    this.tmp3.subVectors(e.coverPos, e.body.position);
    this.tmp3.y = 0;
    const d = this.tmp3.length();
    if (d < 0.7) {
      // Advance along waypoint chain toward the final hide point
      const toHide = Math.hypot(
        e.coverHidePos.x - e.coverPos.x,
        e.coverHidePos.z - e.coverPos.z,
      );
      if (toHide > 0.45) {
        const brush = this.world.brushes.find((b) => b.id === e.coverBrushId);
        const next = brush
          ? nextCoverHop(e.body.position, e.coverHidePos, brush, e.body.position.y)
          : null;
        if (next) {
          e.coverPos.copy(next);
          e.coverCheckPos.copy(e.body.position);
          e.coverCheckT = 0;
          return;
        }
        e.coverPos.copy(e.coverHidePos);
        return;
      }

      e.coverPos.copy(e.coverHidePos);
      e.state = 'cover';
      e.stateT = 0;
      e.coverT = 0.7 + Math.random() * 1.3;
      e.coverWanted = false;
      e.body.velocity.set(0, 0, 0);
      setEnemyCrouch(e, this.world, e.coverIsShort);
      playAimLoco(e, 'aim', 'idle', FADE_COMBAT, true);
      return;
    }

    const speed = dist > 8 ? e.def.runSpeed : e.def.walkSpeed;
    this.steerTo(e, e.coverPos, speed, dt);
    setEnemyCrouch(e, this.world, false);
    playClip(e, speed > 3.5 ? 'run' : 'walk', FADE_LOCO, true);

    // Stuck against geometry — measure real movement, not commanded velocity
    e.coverCheckT += dt;
    if (e.coverCheckT >= 1.35) {
      const moved = Math.hypot(
        e.body.position.x - e.coverCheckPos.x,
        e.body.position.z - e.coverCheckPos.z,
      );
      e.coverCheckPos.copy(e.body.position);
      e.coverCheckT = 0;
      if (moved < 0.45 && d > 0.9) {
        e.coverBrushId = null;
        if (!this.tryBeginSeekCover(e, playerPos)) {
          clearCover(e);
          e.coverCooldownT = 2.5 + Math.random();
          e.state = 'attack';
          e.stateT = 0;
          e.combatDecisionT = 0.35;
        }
      }
    }
  }

  private updateInCover(
    e: EnemyRuntime,
    player: PlayerController,
    _toPlayer: THREE.Vector3,
    _dist: number,
    _los: boolean,
    dt: number,
  ): void {
    e.coverT -= dt;
    // Stay glued near hide point
    this.tmp3.subVectors(e.coverPos, e.body.position);
    this.tmp3.y = 0;
    if (this.tmp3.lengthSq() > 0.04) {
      this.tmp3.normalize();
      e.body.velocity.x = this.tmp3.x * e.def.walkSpeed * 0.6;
      e.body.velocity.z = this.tmp3.z * e.def.walkSpeed * 0.6;
    } else {
      e.body.velocity.x *= 0.4;
      e.body.velocity.z *= 0.4;
    }
    setEnemyCrouch(e, this.world, e.coverIsShort);
    playAimLoco(e, 'aim', 'idle', FADE_COMBAT, true);

    if (e.coverT > 0) return;

    // Recompute peek from current hide — layout may have shifted since seek
    const peek = pickPeekPos(
      this.ray,
      this.world,
      e.coverHidePos,
      player.body.position,
      e.coverIsShort ? 0.95 : 1.4,
    );
    if (!peek) {
      clearCover(e);
      e.coverCooldownT = 1.8 + Math.random();
      setEnemyCrouch(e, this.world, false);
      e.state = 'attack';
      e.combatDecisionT = 0.4;
      return;
    }
    e.peekPos.copy(peek);
    e.state = 'peek';
    e.coverT = 1.0 + Math.random() * 0.8;
    e.burstLeft = 0;
    e.burstPauseT = 0;
    e.attackCd = 0;
    setEnemyCrouch(e, this.world, false);
  }

  private updatePeek(
    e: EnemyRuntime,
    player: PlayerController,
    _toPlayer: THREE.Vector3,
    dist: number,
    _los: boolean,
    dt: number,
  ): void {
    e.coverT -= dt;
    this.tmp3.subVectors(e.peekPos, e.body.position);
    this.tmp3.y = 0;
    const d = this.tmp3.length();
    if (d > 0.25) {
      this.tmp3.multiplyScalar(1 / d);
      e.body.velocity.x = this.tmp3.x * e.def.walkSpeed * 0.9;
      e.body.velocity.z = this.tmp3.z * e.def.walkSpeed * 0.9;
    } else {
      e.body.velocity.x *= 0.3;
      e.body.velocity.z *= 0.3;
    }
    setEnemyCrouch(e, this.world, false);
    playAimLoco(e, 'aim', aimLower(e), FADE_COMBAT, true);

    const peekLos = hasLineOfSight(
      this.ray,
      this.world,
      e.body.position,
      player.body.position,
      eyeHeightFor(e),
    );
    this.tryBurstFire(e, player, dist, peekLos);

    if (
      e.attackPhase !== 'aim' &&
      e.attackPhase !== 'shoot' &&
      e.burstLeft <= 0 &&
      (e.coverT <= 0 || e.attackCd > 0.15)
    ) {
      // Return to hide after the peek burst (or timeout)
      e.state = 'cover';
      e.coverT = 0.8 + Math.random() * 1.4;
      e.burstLeft = 0;
      setEnemyCrouch(e, this.world, e.coverIsShort);
    }
  }

  private tryBurstFire(
    e: EnemyRuntime,
    player: PlayerController,
    dist: number,
    los: boolean,
  ): void {
    if (e.attackLockT > 0 || e.attackCd > 0) return;
    if (!e.alerted) return;
    if (!los || dist > e.def.attackRange) return;
    if (e.attackPhase === 'aim') return;
    if (isAutoRifle(e.kind) && e.burstLeft > 0) {
      this.doGunAttack(e, player);
      this.finishGunShot(e);
      return;
    }
    this.startGunAim(e);
  }

  private startGunAim(e: EnemyRuntime): void {
    if (e.state === 'chase' || e.state === 'roam' || e.state === 'hurt') e.state = 'attack';
    e.attackPhase = 'aim';
    e.attackLockT = GUN_AIM_HOLD;
    e.adsHoldT = Math.max(e.adsHoldT, GUN_AIM_HOLD + 0.4);
    e.pendingDamage = false;
    if (isAutoRifle(e.kind) && e.burstLeft <= 0) e.burstLeft = 3;
    e.body.velocity.x *= 0.28;
    e.body.velocity.z *= 0.28;
    playAimLoco(e, 'aim', aimLower(e), FADE_COMBAT, true);
  }

  private finishGunShot(e: EnemyRuntime): void {
    e.burstPauseT = 0;
    if (isAutoRifle(e.kind)) {
      e.burstLeft = Math.max(0, e.burstLeft - 1);
      e.attackCd = e.burstLeft > 0 ? e.def.fireInterval : 1.05 + Math.random() * 0.55;
      return;
    }
    e.burstLeft = 0;
    e.attackCd = e.def.fireInterval * (0.9 + Math.random() * 0.45);
  }

  private tryBeginSeekCover(e: EnemyRuntime, playerPos: THREE.Vector3): boolean {
    if (e.coverCooldownT > 0) return false;
    const slot = findCoverSlot(this.world, e.body.position, playerPos, this.ray);
    if (!slot) return false;
    e.state = 'seekCover';
    e.stateT = 0;
    e.coverBrushId = slot.brushId;
    e.coverHidePos.copy(slot.hide);
    e.coverPos.copy(slot.approach);
    e.peekPos.copy(slot.peek);
    e.coverIsShort = slot.shortCover;
    e.coverWanted = false;
    e.coverT = 0;
    e.coverCheckPos.copy(e.body.position);
    e.coverCheckT = 0;
    setEnemyCrouch(e, this.world, false);
    return true;
  }

  /**
   * Player gunfire — first bang can surprise; later bangs only retarget.
   * Alerted guards who cannot see Bond run to the new shot instead of flinching.
   */
  hearShot(origin: THREE.Vector3, player: PlayerController, silenced = false): void {
    const ppos = player.body.position;
    this.playerKnown.copy(ppos);
    this.listenerPos.copy(player.camera.position);
    const rangeScale = silenced ? 0.32 : 1;
    for (const e of this.enemies) {
      if (e.dead || e.fleeing) continue;
      const friendly = isFriendlyKind(e.kind);
      const dist = hearDist(origin, e.body.position);
      const closeSilenced = silenced && dist <= SILENCED_ALERT;
      const hearR = (friendly ? HEAR_GUNSHOT_FRIENDLY : HEAR_GUNSHOT) * rangeScale;
      if (dist > hearR) continue;
      if (!closeSilenced && !this.soundCarries(origin, e.body.position, silenced ? 0 : HEAR_GUNSHOT_HOPS)) {
        continue;
      }
      if (e.quietGearKnock) {
        e.quietGearKnock = false;
        continue;
      }

      if (e.toiletPhase === 'sit') {
        this.startToiletCheck(e, origin, 'shot');
        continue;
      }
      if (e.toiletPhase !== 'none') continue;

      if (!friendly && this.alarms?.sounded && !e.alerted) {
        this.becomeAlerted(e, player.camera.position, true, true);
        continue;
      }

      if (!friendly && e.alerted) {
        this.tmp.set(ppos.x - e.body.position.x, 0, ppos.z - e.body.position.z);
        const pDist = this.tmp.length();
        const toPlayer = this.tmp2.copy(this.tmp);
        if (pDist > 1e-4) toPlayer.multiplyScalar(1 / pDist);
        const los = hasLineOfSight(this.ray, this.world, e.body.position, ppos, eyeHeightFor(e));
        if (canSeePlayer(e, toPlayer, pDist, los, player.crouchBlend > 0.5)) {
          e.lostSightT = 0;
          e.lastKnown.copy(ppos);
          continue;
        }
        this.retargetToSound(e, origin, dist);
        continue;
      }
      if (!friendly) {
        this.tmp.set(ppos.x - e.body.position.x, 0, ppos.z - e.body.position.z);
        const pDist = this.tmp.length();
        const toPlayer = this.tmp2.copy(this.tmp);
        if (pDist > 1e-4) toPlayer.multiplyScalar(1 / pDist);
        const los = hasLineOfSight(this.ray, this.world, e.body.position, ppos, eyeHeightFor(e));
        if (canSeePlayer(e, toPlayer, pDist, los, player.crouchBlend > 0.5)) {
          this.becomeAlerted(e, player.camera.position);
          continue;
        }
        if (closeSilenced) {
          this.becomeAlerted(e, player.camera.position, true, true);
          this.playLine(e, VOICE_SETS.silenced_close!, true);
          continue;
        }
      }

      const surprise = !e.heardGunshot;
      if (!surprise) {
        this.retargetToSound(e, origin, dist);
        continue;
      }
      e.heardGunshot = true;
      guessHeardPos(origin, dist, e.heardPos);
      this.beginInvestigate(e, e.heardPos, true, dist <= HEAR_GUNSHOT_EXPLORE * rangeScale, true);
      setEnemyFaceMood(e.face, 'scared');
      this.playLine(e, friendly ? VOICE_SETS.scared_shot! : VOICE_SETS.curious!, true);
    }
  }

  /**
   * Same room or through open doorways. A closed stall or room door stops the path.
   * Unknown rooms do not carry — better to miss a hear than leak through a floor.
   */
  private soundCarries(origin: THREE.Vector3, listener: THREE.Vector3, maxHops = HEAR_GUNSHOT_HOPS): boolean {
    if (this.stallBlocksHear(origin, listener)) return false;
    if (this.rooms.length === 0) return true;
    const from = roomAt(this.rooms, origin);
    const to = roomAt(this.rooms, listener);
    if (!from || !to) return false;
    if (from.id === to.id) return true;
    return this.openHopCount(from.id, to.id) <= maxHops;
  }

  /** Closed WC leaf: inside the bay cannot hear / be heard by anyone outside. */
  private stallBlocksHear(a: THREE.Vector3, b: THREE.Vector3): boolean {
    if (!this.doors) return false;
    for (const d of this.doors.hearDoors()) {
      if (d.style !== 'stall' || d.ajar) continue;
      const inA = inStallBay(a, d.hinge.x + d.width * 0.5);
      const inB = inStallBay(b, d.hinge.x + d.width * 0.5);
      if (inA !== inB) return true;
    }
    return false;
  }

  private portalDoorClosed(fromId: string, toId: string): boolean {
    if (!this.doors) return false;
    const portals = this.roomPortals.filter((p) => p.from === fromId && p.to === toId);
    if (portals.length === 0) return false;
    for (const d of this.doors.hearDoors()) {
      if (d.style === 'stall' || d.ajar) continue;
      for (const p of portals) {
        if (p.box.containsPoint(d.mid) || p.box.containsPoint(d.hinge) || p.box.distanceToPoint(d.mid) < 1.4) {
          return true;
        }
      }
    }
    return false;
  }

  private openHopCount(fromId: string, toId: string): number {
    if (fromId === toId) return 0;
    const seen = new Set([fromId]);
    let frontier = [fromId];
    let hops = 0;
    while (frontier.length) {
      hops += 1;
      const next: string[] = [];
      for (const id of frontier) {
        for (const n of this.roomAdj.get(id) ?? []) {
          if (this.portalDoorClosed(id, n)) continue;
          if (n === toId) return hops;
          if (seen.has(n)) continue;
          seen.add(n);
          next.push(n);
        }
      }
      frontier = next;
    }
    return Infinity;
  }

  /** Punching glass, a cistern, a crate, or a lamp. */
  hearSmash(origin: THREE.Vector3): void {
    this.hearNoise(origin, 'smash');
  }

  /** Fist on a wall or door — nearby unalerted people get suspicious. */
  hearThud(origin: THREE.Vector3): void {
    this.hearNoise(origin, 'thud');
  }

  private hearNoise(origin: THREE.Vector3, kind: 'smash' | 'thud'): void {
    const range = kind === 'smash' ? HEAR_SMASH : HEAR_THUD;
    const hops = kind === 'smash' ? HEAR_GUNSHOT_HOPS : 1;
    for (const e of this.enemies) {
      if (e.dead || e.fleeing) continue;
      const dist = hearDist(origin, e.body.position);
      if (dist > range) continue;
      if (!this.soundCarries(origin, e.body.position, hops)) continue;

      if (e.toiletPhase === 'sit') {
        this.startToiletCheck(e, origin, 'vandal');
        continue;
      }
      if (e.toiletPhase !== 'none' || e.alerted) continue;

      const fresh = e.investigateT <= 0;
      guessHeardPos(origin, dist, e.heardPos);
      this.beginInvestigate(e, e.heardPos, false, true);
      if (!fresh) continue;
      setEnemyFaceMood(e.face, 'scared');
      this.playLine(e, VOICE_SETS.curious!, true);
    }
  }

  private updateToiletSitter(e: EnemyRuntime, player: PlayerController, dt: number): void {
    e.toiletT += dt;
    const p = player.body.position;
    const inStall =
      p.x > e.toiletBayX - 1.2 &&
      p.x < e.toiletBayX + 1.2 &&
      p.z > 1.15 &&
      p.z < 4.05 &&
      p.y < 2.6;
    const doorOpened = !!e.toiletDoorId && (this.doors?.isAjar(e.toiletDoorId) ?? false);

    if (e.toiletPhase === 'sit') {
      e.body.velocity.set(0, 0, 0);
      e.sitBlend = 1;
      if (inStall || doorOpened) this.startToiletDisturb(e);
      return;
    }

    if (
      (e.toiletPhase === 'noise' || e.toiletPhase === 'rise' || e.toiletPhase === 'leave') &&
      inStall
    ) {
      this.doors?.openById(e.toiletDoorId);
      this.finishToiletScript(e);
      this.becomeAlerted(e, player.camera.position, true, true);
      return;
    }

    if (e.toiletPhase === 'occupied') {
      e.body.velocity.set(0, 0, 0);
      e.sitBlend = 1;
      if (this.toiletLineDone(e, 0.55)) {
        e.toiletPhase = 'recognize';
        e.toiletT = 0;
        setEnemyFaceMood(e.face, 'scared');
        this.playLine(e, ['voice_toilet_recognize'], true);
      }
      return;
    }

    if (e.toiletPhase === 'recognize') {
      e.body.velocity.set(0, 0, 0);
      e.sitBlend = 1;
      if (this.toiletLineDone(e, 0.45)) {
        e.toiletPhase = 'flush';
        e.toiletT = 0;
        setEnemyFaceMood(e.face, 'angry');
        this.audio.playToiletFlush(e.root.position);
      }
      return;
    }

    if (e.toiletPhase === 'flush') {
      e.body.velocity.set(0, 0, 0);
      e.sitBlend = 1;
      if (e.toiletT >= 0.35) {
        e.toiletPhase = 'stand';
        e.toiletT = 0;
        playClip(e, 'guard', FADE_COMBAT, true);
        this.playLine(e, ['voice_toilet_attack'], true);
        this.doors?.openById(e.toiletDoorId);
      }
      return;
    }

    if (e.toiletPhase === 'stand') {
      e.body.velocity.set(0, 0, 0);
      e.sitBlend = Math.max(0, 1 - e.toiletT / 1.2);
      this.doors?.openById(e.toiletDoorId);
      if (e.toiletT >= 1.2) {
        this.finishToiletScript(e);
        this.becomeAlerted(e, player.camera.position, true, true);
      }
      return;
    }

    if (e.toiletPhase === 'noise') {
      e.body.velocity.set(0, 0, 0);
      e.sitBlend = 1;
      if (this.toiletLineDone(e, 0.4)) {
        e.toiletPhase = 'rise';
        e.toiletT = 0;
        playClip(e, 'guard', FADE_LOCO, true);
        this.doors?.openById(e.toiletDoorId);
      }
      return;
    }

    if (e.toiletPhase === 'rise') {
      e.body.velocity.set(0, 0, 0);
      e.sitBlend = Math.max(0, 1 - e.toiletT / 1.0);
      this.doors?.openById(e.toiletDoorId);
      if (this.tryToiletSpot(e, player)) return;
      if (e.toiletT >= 1.0) {
        e.toiletPhase = 'leave';
        e.toiletT = 0;
      }
      return;
    }

    // Walk out of the stall, then check the noise.
    e.sitBlend = 0;
    this.doors?.openById(e.toiletDoorId);
    this.doors?.openNear(e.body.position, 1.7);
    if (this.tryToiletSpot(e, player)) return;
    const tx = e.toiletBayX;
    const tz = 5.15;
    this.tmp.set(tx - e.body.position.x, 0, tz - e.body.position.z);
    const d = this.tmp.length();
    if (d < 0.42 || e.toiletT > 5) {
      if (this.tryToiletSpot(e, player)) return;
      const dest = e.heardPos.clone();
      this.finishToiletScript(e);
      this.beginInvestigate(e, dest, false, true);
      return;
    }
    this.tmp.multiplyScalar(1 / d);
    e.yaw = Math.atan2(-this.tmp.x, -this.tmp.z);
    const speed = e.def.walkSpeed * 0.8;
    e.body.velocity.x = this.tmp.x * speed;
    e.body.velocity.z = this.tmp.z * speed;
    this.world.move(e.body, dt);
    playClip(e, 'walk', FADE_LOCO, true);
  }

  private startToiletDisturb(e: EnemyRuntime): void {
    if (e.toiletPhase !== 'sit') return;
    e.toiletPhase = 'occupied';
    e.toiletT = 0;
    setEnemyFaceMood(e.face, 'angry');
    this.playLine(e, ['voice_toilet_occupied'], true);
    this.audio.playAt(e.def.alertSfx, e.root.position, this.listenerPos, {
      volume: 0.28,
      rate: 1.15 + Math.random() * 0.1,
      range: 28,
    });
  }

  /** Door is open and he's on his feet — any clear view of the player is a spot. */
  private tryToiletSpot(e: EnemyRuntime, player: PlayerController): boolean {
    const ppos = player.body.position;
    const inDoorway =
      Math.abs(ppos.x - e.toiletBayX) < 0.85 && ppos.z >= 3.7 && ppos.z <= 6.5 && ppos.y < 2.6;
    this.tmp.set(ppos.x - e.body.position.x, 0, ppos.z - e.body.position.z);
    const dist = this.tmp.length();
    if (!inDoorway) {
      if (dist > SIGHT_RANGE) return false;
      const los = hasLineOfSight(this.ray, this.world, e.body.position, ppos, eyeHeightFor(e));
      if (!los) return false;
      if (dist > 7) {
        const toPlayer = this.tmp2.copy(this.tmp);
        if (dist > 1e-4) toPlayer.multiplyScalar(1 / dist);
        if (!canSeePlayer(e, toPlayer, dist, true, player.crouchBlend > 0.5)) return false;
      }
    }
    this.finishToiletScript(e);
    setEnemyFaceMood(e.face, 'angry');
    this.becomeAlerted(e, player.camera.position, true, false);
    return true;
  }

  private startToiletCheck(e: EnemyRuntime, origin: THREE.Vector3, kind: 'shot' | 'vandal'): void {
    if (e.toiletPhase !== 'sit') return;
    e.heardPos.copy(origin);
    e.lastKnown.copy(origin);
    if (kind === 'shot') e.heardGunshot = true;
    e.toiletPhase = 'noise';
    e.toiletT = 0;
    setEnemyFaceMood(e.face, kind === 'shot' ? 'scared' : 'angry');
    this.playLine(e, kind === 'shot' ? VOICE_SETS.toilet_shot! : VOICE_SETS.toilet_noise!, true);
  }

  private toiletLineDone(e: EnemyRuntime, minHold: number): boolean {
    return e.toiletT >= minHold && e.talkT <= 0.12;
  }

  private finishToiletScript(e: EnemyRuntime): void {
    e.toiletPhase = 'none';
    e.sitBlend = 0;
    e.toiletT = 0;
  }

  /** Later shots: no flinch / bark — just walk to the new bang. */
  private retargetToSound(e: EnemyRuntime, origin: THREE.Vector3, dist: number): void {
    guessHeardPos(origin, dist, e.heardPos);
    e.lastKnown.copy(e.heardPos);
    e.lostSightT = 0;
    e.heardGunshot = true;
    e.lookaroundT = 0;
    e.pointT = 0;
    e.pendingLookaround = false;
    e.investigateT = INVESTIGATE_SHOT;
    clearNavFollow(e.nav);
    if (e.alerted) e.state = 'chase';
    if (e.barkCd <= 0 && Math.random() < 0.4) this.barkHunt(e);
  }

  /** Nearby hostiles hear the victim's Ow and go hostile. */
  private alertNearHurt(victim: EnemyRuntime): void {
    const origin = victim.body.position;
    for (const e of this.enemies) {
      if (e === victim || e.dead || e.fleeing || e.alerted) continue;
      if (hearDist(origin, e.body.position) > HEAR_HURT) continue;
      if (!this.soundCarries(origin, e.body.position, 1)) continue;

      if (e.toiletPhase === 'sit') {
        this.startToiletCheck(e, origin, 'shot');
        continue;
      }
      if (e.toiletPhase !== 'none') continue;
      if (isFriendlyKind(e.kind)) {
        guessHeardPos(origin, hearDist(origin, e.body.position), e.heardPos);
        this.beginInvestigate(e, e.heardPos, true, true);
        continue;
      }
      this.becomeAlerted(e, this.playerKnown, true, true);
    }
  }

  private reactToHeadgearKnock(e: EnemyRuntime): void {
    if (isFriendlyKind(e.kind)) {
      if (!e.fleeing) this.beginFlee(e, 'protest');
      return;
    }
    const wasAlerted = e.alerted;
    this.aggroOnAttacker(e);
    if (!this.tryAlarmRun(e, wasAlerted ? 0.12 : 0.48)) {
      this.playLine(e, VOICE_SETS.hat_spot!, true);
    }
  }

  private tryAlarmRun(e: EnemyRuntime, chance: number): boolean {
    if (e.dead || e.fleeing || e.alarmRun || e.kind === 'melee' || isFriendlyKind(e.kind)) return false;
    if (this.alarms?.sounded) return false;
    const stand = this.alarms?.nearestStand(e.body.position);
    if (!stand) return false;
    if (Math.random() > chance) return false;
    e.alarmRun = true;
    e.state = 'alarm';
    e.attackPhase = 'none';
    e.pendingDamage = false;
    e.attackLockT = 0;
    e.coverWanted = false;
    e.pointT = 0;
    e.pendingLookaround = false;
    clearCover(e);
    setEnemyCrouch(e, this.world, false);
    this.playLine(e, VOICE_SETS.sound_alarm!, true);
    playClip(e, 'run', FADE_LOCO, true);
    return true;
  }

  private updateAlarmRun(e: EnemyRuntime, dt: number): void {
    if (this.alarms?.sounded) {
      e.alarmRun = false;
      e.state = 'chase';
      return;
    }
    const stand = this.alarms?.nearestStand(e.body.position);
    if (!stand) {
      e.alarmRun = false;
      e.state = 'chase';
      return;
    }
    const dist = horizontalDist(e.body.position, stand);
    if (dist < 1.15) {
      this.alarms?.sound(e.root.position);
      e.alarmRun = false;
      e.state = 'chase';
      return;
    }
    this.steerTo(e, stand, e.def.runSpeed, dt);
    this.world.move(e.body, dt);
    syncRoot(e);
    playClip(e, 'run', FADE_LOCO, true);
    this.tmp.set(stand.x, stand.y + 1.4, stand.z);
    applyLookAt(e, this.tmp, dt);
  }

  /** Klaxon: every living hostile is combat-ready; friendlies get scared. */
  alertFacility(): void {
    for (const e of this.enemies) {
      e.alarmRun = false;
      if (e.dead) continue;
      if (e.toiletPhase !== 'none') this.finishToiletScript(e);
      if (isFriendlyKind(e.kind)) {
        if (e.investigateT <= 0) this.beginInvestigate(e, this.playerKnown, true, true);
        continue;
      }
      if (e.fleeing) continue;
      if (!e.alerted) this.becomeAlerted(e, this.playerKnown, true, true);
      else {
        e.lostSightT = 0;
        e.lastKnown.copy(this.playerKnown);
        if (e.state === 'alarm') e.state = 'chase';
      }
    }
  }

  /** Damage (or a headgear knock) locks combat onto the player. */
  private aggroOnAttacker(e: EnemyRuntime): void {
    if (e.dead || e.fleeing || isFriendlyKind(e.kind)) return;
    this.finishToiletScript(e);
    if (!e.alerted) {
      this.becomeAlerted(e, this.playerKnown, true, true);
    } else {
      e.lostSightT = 0;
      e.lastKnown.copy(this.playerKnown);
    }
  }

  private becomeAlerted(
    e: EnemyRuntime,
    listener?: THREE.Vector3,
    skipPoint = false,
    skipBark = false,
  ): void {
    if (e.alerted || isFriendlyKind(e.kind) || e.fleeing) return;
    e.alerted = true;
    e.investigateT = 0;
    e.lostSightT = 0;
    if (listener) e.lastKnown.set(listener.x, e.body.position.y, listener.z);
    clearNavFollow(e.nav);
    e.combatAge = 0;
    e.pointT = skipPoint || e.kind === 'melee' ? 0 : 0.85 + Math.random() * 0.35;
    e.pendingLookaround = !skipPoint && e.kind !== 'melee' && e.actions.has('lookaround') && Math.random() < 0.5;
    setEnemyFaceMood(e.face, 'angry');
    if (!skipBark) {
      if (this.playerFistsUp) this.barkFistsTaunt(e);
      else this.bark(e, listener);
    }
    e.state = 'chase';
    playClip(e, skipPoint || e.kind === 'melee' ? 'guard' : 'point', FADE_LOCO, true);
  }

  private calmDown(e: EnemyRuntime): void {
    e.alerted = false;
    e.alarmRun = false;
    e.heardGunshot = false;
    e.lostSightT = 0;
    e.pointT = 0;
    e.pendingLookaround = false;
    e.lookaroundT = 0;
    e.combatAge = 0;
    e.fistsTauntSaid = false;
    e.attackPhase = 'none';
    e.pendingDamage = false;
    e.attackLockT = 0;
    e.state = 'roam';
    e.roamWaitT = 0.5 + Math.random() * 0.8;
    clearNavFollow(e.nav);
    clearCover(e);
    setEnemyCrouch(e, this.world, false);
    setEnemyFaceMood(e.face, 'calm');
    setEnemyMouthFrame(e.face, 0);
    e.body.velocity.set(0, 0, 0);
    playClip(e, 'idle', FADE_LOCO, true);
  }

  private fistsTauntSet(e: EnemyRuntime): string[] {
    return e.kind === 'melee' ? VOICE_SETS.melee_box! : VOICE_SETS.boxing!;
  }

  private barkFistsTaunt(e: EnemyRuntime): void {
    e.fistsTauntSaid = true;
    this.playLine(e, this.fistsTauntSet(e), true);
    e.barkCd = BARK_COOLDOWN;
  }

  private isComeOutPlaying(e: EnemyRuntime): boolean {
    return e.talkT > 0 && COME_OUT_KEYS.has(e.lastBark);
  }

  private barkHunt(e: EnemyRuntime): void {
    if (isFriendlyKind(e.kind) || e.dead || e.fleeing) return;
    const set = VOICE_SETS.come_out;
    if (!set) return;
    this.playLine(e, set);
  }

  private barkSpotted(e: EnemyRuntime): void {
    this.playLine(e, VOICE_SETS.spotted!, true);
    e.barkCd = BARK_COOLDOWN;
  }

  private cutVoice(e: EnemyRuntime): void {
    e.stopTalk?.();
    e.stopTalk = null;
    e.talkT = 0;
    e.talkMouthT = 0;
    setEnemyMouthFrame(e.face, 0);
  }

  private startVoice(e: EnemyRuntime, key: string, listener?: THREE.Vector3): void {
    this.cutVoice(e);
    e.lastBark = key;
    const played = this.audio.playAt(key, e.root.position, listener ?? this.listenerPos, {
      volume: 0.95,
      range: 52,
      refDistance: 7,
      rolloff: 0.35,
    });
    e.stopTalk = played.stop;
    e.talkT = Math.max(played.duration, 0.7);
    e.talkMouthT = 0;
  }

  private bark(e: EnemyRuntime, listener?: THREE.Vector3): void {
    if (e.barkCd > 0 || e.dead) return;
    const set = (VOICE_SETS[e.kind] ?? VOICE_SETS.pistol!).filter((k) => !COME_OUT_KEYS.has(k));
    if (set.length === 0) return;
    const choices = set.filter((k) => k !== e.lastBark);
    const key = (choices.length ? choices : set)[Math.floor(Math.random() * (choices.length || set.length))]!;
    e.barkCd = BARK_COOLDOWN;
    this.startVoice(e, key, listener);
  }

  private playLine(e: EnemyRuntime, set: string[], ignoreCd = false): void {
    if (e.dead || set.length === 0) return;
    if (!ignoreCd && e.barkCd > 0) return;
    const choices = set.filter((k) => k !== e.lastBark);
    const key = (choices.length ? choices : set)[Math.floor(Math.random() * (choices.length || set.length))]!;
    if (!ignoreCd) e.barkCd = BARK_COOLDOWN;
    this.startVoice(e, key);
  }

  private playHurtVoice(e: EnemyRuntime, force = false): void {
    if (!force && e.hurtBarkCd > 0) return;
    e.hurtBarkCd = force ? 0.2 : 0.48;
    const set =
      e.kind === 'shotgun' || e.kind === 'sawedoff'
        ? VOICE_SETS.hurt_shotgun!
        : e.kind === 'melee'
          ? VOICE_SETS.hurt_melee!
          : isFriendlyKind(e.kind)
            ? VOICE_SETS[e.kind]!
            : VOICE_SETS.hurt_pistol!;
    this.playLine(e, set, true);
  }

  private tryStartCombatFlee(e: EnemyRuntime): boolean {
    if (e.brokeAndRan || e.fleeing || isFriendlyKind(e.kind)) return false;
    if (e.kind === 'melee' && Math.random() > 0.06) return false;
    if (e.hp > e.def.hp * 0.3) return false;
    if (Math.random() > 0.35) return false;
    this.beginFlee(e, 'combat');
    return true;
  }

  private beginFlee(e: EnemyRuntime, reason: 'combat' | 'protest'): void {
    e.fleeing = true;
    e.brokeAndRan = true;
    e.alarmRun = false;
    e.alerted = false;
    e.pointT = 0;
    e.pendingLookaround = false;
    e.attackPhase = 'none';
    e.pendingDamage = false;
    e.attackLockT = 0;
    e.coverWanted = false;
    clearCover(e);
    setEnemyCrouch(e, this.world, false);
    e.state = 'flee';
    e.stateT = 0;
    setEnemyFaceMood(e.face, 'scared');
    this.playLine(e, reason === 'protest' ? (VOICE_SETS[e.kind] ?? VOICE_SETS.civilian!) : VOICE_SETS.flee!, true);
    playClip(e, 'run', FADE_LOCO, true);
  }

  private updateFlee(
    e: EnemyRuntime,
    player: PlayerController,
    _toPlayer: THREE.Vector3,
    dist: number,
    dt: number,
  ): void {
    if (e.nav.path.length === 0) {
      this.nav.pickFlee(e.body.position, player.body.position, e.nav.goal);
    }
    this.steerTo(e, e.nav.goal, e.def.runSpeed, dt);
    this.world.move(e.body, dt);
    syncRoot(e);
    playClip(e, 'run', FADE_LOCO, true);
    applyLookAt(e, player.camera.position, dt);
    if (dist > 14 && e.stateT > 4) {
      e.body.velocity.set(0, 0, 0);
      playClip(e, 'guard', FADE_LOCO, true);
    }
  }

  private updateFriendly(
    e: EnemyRuntime,
    player: PlayerController,
    toPlayer: THREE.Vector3,
    dist: number,
    dt: number,
  ): void {
    player.camera.getWorldDirection(this.tmp3);
    const looking = this.tmp3.x * toPlayer.x + this.tmp3.z * toPlayer.z > 0.82;
    if (dist < 3.8 && looking) {
      e.yaw = Math.atan2(-toPlayer.x, -toPlayer.z);
      e.body.velocity.x *= 0.4;
      e.body.velocity.z *= 0.4;
      this.world.move(e.body, dt);
      syncRoot(e);
      playClip(e, 'guard', FADE_LOCO, true);
      applyLookAt(e, player.camera.position, dt);
      if (!e.handsUpSaid) {
        e.handsUpSaid = true;
        this.playLine(e, e.kind === 'scientist' ? ['voice_friendly_sci_5'] : ['voice_friendly_civ_6'], true);
      }
      return;
    }
    this.updateRoam(e, dt);
    applyLookAt(e, player.camera.position, dt);
  }

  private tickTalk(e: EnemyRuntime, dt: number): void {
    e.barkCd = Math.max(0, e.barkCd - dt);
    if (e.talkT <= 0) return;
    e.talkT -= dt;
    e.talkMouthT -= dt;
    if (e.talkT <= 0) {
      e.stopTalk = null;
      setEnemyMouthFrame(e.face, 0);
      return;
    }
    if (e.talkMouthT <= 0) {
      e.talkMouthT = 0.07 + Math.random() * 0.06;
      const frame = (1 + Math.floor(Math.random() * 3)) as MouthFrame;
      setEnemyMouthFrame(e.face, frame);
    }
  }

  private beginInvestigate(
    e: EnemyRuntime,
    pos: THREE.Vector3,
    fromShot: boolean,
    explore = true,
    surprise = true,
  ): void {
    e.heardPos.copy(pos);
    e.lastKnown.copy(pos);
    clearNavFollow(e.nav);
    if (!surprise) {
      e.lookaroundT = 0;
      e.investigateT = fromShot ? INVESTIGATE_SHOT : INVESTIGATE_TIME;
      return;
    }
    if (e.investigateT <= 0) {
      e.investigateT = fromShot ? INVESTIGATE_SHOT : INVESTIGATE_TIME;
      if (fromShot) {
        e.lookaroundT = 0.28 + Math.random() * 0.28;
      } else {
        e.lookaroundT = Math.random() < 0.45 ? 0.7 + Math.random() * 0.9 : 0;
      }
      if (!explore) {
        e.lookaroundT = Math.max(e.lookaroundT, 1.15);
        e.investigateT = e.lookaroundT + 0.4;
      }
      if (e.lookaroundT > 0 && e.actions.has('lookaround')) {
        e.body.velocity.set(0, 0, 0);
        playClip(e, 'lookaround', FADE_LOCO, false);
      }
    } else {
      e.investigateT = Math.max(e.investigateT, fromShot ? INVESTIGATE_SHOT * 0.55 : INVESTIGATE_TIME * 0.55);
    }
  }

  /** Civilians: glance at the bang, then run away from where they think it was. */
  private updateScaredHear(e: EnemyRuntime, dt: number): void {
    e.investigateT -= dt;
    this.tmp.subVectors(e.body.position, e.heardPos);
    this.tmp.y = 0;
    const d = this.tmp.length();
    if (d > 1e-4) this.tmp.multiplyScalar(1 / d);
    else this.tmp.set(1, 0, 0);

    if (e.lookaroundT > 0) {
      e.lookaroundT -= dt;
      e.yaw = Math.atan2(this.tmp.x, this.tmp.z);
      e.body.velocity.x *= 0.45;
      e.body.velocity.z *= 0.45;
      this.world.move(e.body, dt);
      syncRoot(e);
      return;
    }

    if (e.investigateT <= 0) {
      e.body.velocity.set(0, 0, 0);
      this.world.move(e.body, dt);
      syncRoot(e);
      playClip(e, 'idle', FADE_LOCO, true);
      return;
    }

    const speed = d < 11 ? e.def.runSpeed : e.def.walkSpeed;
    if (e.nav.path.length === 0) {
      this.nav.pickFlee(e.body.position, e.heardPos, e.nav.goal);
    }
    this.steerTo(e, e.nav.goal, speed, dt);
    this.world.move(e.body, dt);
    syncRoot(e);
    playClip(e, d < 11 ? 'run' : 'walk', FADE_LOCO, true);
  }

  private updateInvestigate(e: EnemyRuntime, dt: number): void {
    e.investigateT -= dt;
    const d = horizontalDist(e.body.position, e.heardPos);

    if (e.lookaroundT > 0) {
      e.lookaroundT -= dt;
      e.body.velocity.x *= 0.5;
      e.body.velocity.z *= 0.5;
      this.world.move(e.body, dt);
      syncRoot(e);
      return;
    }

    if (d < 0.55 || e.investigateT <= 0) {
      e.investigateT = 0;
      e.roamWaitT = 0.7 + Math.random() * 1.2;
      e.body.velocity.set(0, 0, 0);
      this.world.move(e.body, dt);
      syncRoot(e);
      playClip(e, 'idle', FADE_LOCO, true);
      return;
    }

    if (e.heardGunshot && e.barkCd <= 0 && Math.random() < 0.08) this.barkHunt(e);
    this.steerTo(e, e.heardPos, e.heardGunshot ? e.def.walkSpeed : e.def.walkSpeed * 0.65, dt);
    this.world.move(e.body, dt);
    syncRoot(e);
    playClip(e, 'walk', FADE_LOCO, true);
  }

  private updateRoam(e: EnemyRuntime, dt: number): void {
    e.state = 'roam';
    if (e.roamWaitT > 0) {
      e.roamWaitT -= dt;
      e.body.velocity.x *= 0.8;
      e.body.velocity.z *= 0.8;
      this.world.move(e.body, dt);
      syncRoot(e);
      playClip(e, e.roamFidget && e.actions.has('lookaround') ? 'lookaround' : 'idle', FADE_LOCO, true);
      if (e.roamWaitT <= 0) {
        pickRoamTarget(e);
        clearNavFollow(e.nav);
      }
      return;
    }

    const d = horizontalDist(e.body.position, e.roamTarget);
    if (d < 0.45) {
      e.roamWaitT = 1 + Math.random() * 2;
      e.roamFidget = Math.random() < 0.5;
      e.body.velocity.x *= 0.5;
      e.body.velocity.z *= 0.5;
      this.world.move(e.body, dt);
      syncRoot(e);
      playClip(e, e.roamFidget && e.actions.has('lookaround') ? 'lookaround' : 'idle', FADE_LOCO, true);
      return;
    }

    this.steerTo(e, e.roamTarget, e.def.walkSpeed * 0.75, dt);
    this.world.move(e.body, dt);
    syncRoot(e);
    playClip(e, 'walk', FADE_LOCO, true);
  }

  /** Straight at dest if the floor is clear; otherwise A* hop. */
  private chaseToward(
    e: EnemyRuntime,
    dest: THREE.Vector3,
    toDest: THREE.Vector3,
    speed: number,
    los: boolean,
    dt: number,
  ): void {
    if (los && this.nav.hasWalk(e.body.position, dest)) {
      e.body.velocity.x = toDest.x * speed;
      e.body.velocity.z = toDest.z * speed;
      return;
    }
    this.steerTo(e, dest, speed, dt);
  }

  /** Follow the waypoint graph toward dest (falls back to a straight line). */
  private steerTo(e: EnemyRuntime, dest: THREE.Vector3, speed: number, dt: number): void {
    if (!this.nav.nextHop(e.body.position, dest, e.nav, dt, this.hop)) {
      e.body.velocity.x *= 0.5;
      e.body.velocity.z *= 0.5;
      return;
    }
    this.tmp.set(this.hop.x - e.body.position.x, 0, this.hop.z - e.body.position.z);
    const d = this.tmp.length();
    if (d < 1e-4) {
      e.body.velocity.x *= 0.5;
      e.body.velocity.z *= 0.5;
      return;
    }
    this.tmp.multiplyScalar(1 / d);
    e.body.velocity.x = this.tmp.x * speed;
    e.body.velocity.z = this.tmp.z * speed;
    e.yaw = Math.atan2(-this.tmp.x, -this.tmp.z);
  }

  private startMeleeAttack(e: EnemyRuntime): void {
    const next = nextPunchCombo(e.punchComboStep);
    e.punchId = next.id;
    e.punchComboStep = next.nextStep;
    e.state = 'attack';
    e.attackCd = e.def.fireInterval * (0.9 + Math.random() * 0.35);
    e.attackPhase = 'swing';
    e.attackLockT = punchPlayDuration(bakedPunchClip(next.id));
    e.pendingDamage = true;
    playClip(e, 'melee', FADE_HURT, false);
    this.audio.playAt('meleeSwing', e.root.position, this.listenerPos, {
      volume: 0.42,
      rate: punchSwingRate(next.id) + (Math.random() - 0.5) * 0.04,
      range: 28,
    });
  }

  private doGunAttack(e: EnemyRuntime, player: PlayerController): void {
    if (!e.alerted) return;
    // Preserve cover/peek/duck — only default open fight to `attack`
    if (e.state === 'chase' || e.state === 'roam' || e.state === 'hurt') e.state = 'attack';
    e.attackPhase = 'shoot';
    e.attackLockT = e.kind === 'mp5' || e.kind === 'ak47' ? 0.08 : SHOOT_LOCK;
    e.adsHoldT = Math.max(e.adsHoldT, e.attackLockT + 0.38);
    e.weaponRecoil = 1;

    const eye = player.camera.position;
    const ox = e.body.position.x;
    const oy = e.body.position.y + eyeHeightFor(e);
    const oz = e.body.position.z;
    const origin = this.tmp.set(ox, oy, oz);
    const dir = this.tmp2.subVectors(eye, origin).normalize();
    // Intentionally sloppy — GoldenEye guards spray wide of the player
    const shotgunLike = e.kind === 'shotgun' || e.kind === 'sawedoff';
    const spread = shotgunLike ? 0.22 : e.kind === 'mp5' || e.kind === 'ak47' ? 0.16 : 0.14;
    dir.x += (Math.random() - 0.5) * spread;
    dir.y += (Math.random() - 0.5) * (spread * 0.55);
    dir.z += (Math.random() - 0.5) * spread;
    dir.normalize();

    const pellets = e.kind === 'shotgun' ? 5 : e.kind === 'sawedoff' ? 7 : 1;
    let hitPlayer = false;
    const hitRadius = shotgunLike ? 0.55 : 0.3;
    const muzzle = enemyMuzzleWorld(e, origin);
    const lineKind: ShotLineKind = shotgunLike ? 'shotgun' : e.kind === 'mp5' || e.kind === 'ak47' ? 'rifle' : 'pistol';
    const range = e.def.attackRange + 8;
    for (let i = 0; i < pellets; i++) {
      const d = dir.clone();
      if (pellets > 1) {
        d.x += (Math.random() - 0.5) * 0.2;
        d.y += (Math.random() - 0.5) * 0.12;
        d.z += (Math.random() - 0.5) * 0.2;
        d.normalize();
      }
      this.ray.set(origin, d);
      this.ray.far = e.def.attackRange + 2;
      const toEye = eye.clone().sub(origin);
      const t = toEye.dot(d);
      let pelletHitPlayer = false;
      if (t > 0.5 && t < this.ray.far) {
        const closest = origin.clone().addScaledVector(d, t);
        if (closest.distanceTo(eye) < hitRadius) {
          // Pellet must reach the player before any solid — including floors/ceilings
          if (!firstWallHit(this.world, ox, oy, oz, d.x, d.y, d.z, t - 0.08, _wallHit)) {
            hitPlayer = true;
            pelletHitPlayer = true;
          }
        }
      }
      let end: THREE.Vector3;
      if (pelletHitPlayer) {
        end = eye;
      } else if (firstWallHit(this.world, ox, oy, oz, d.x, d.y, d.z, range, _pelletHit)) {
        end = _pelletHit;
      } else {
        end = _pelletHit.copy(origin).addScaledVector(d, range);
      }
      this.shotLines?.spawn(muzzle, end, lineKind);
    }
    if (hitPlayer) {
      player.takeDamage(e.def.damage, e.body.position);
      this.audio.play('hitBody', { volume: 0.56, rate: 0.94 + Math.random() * 0.12 });
    } else if (
      firstWallHit(this.world, ox, oy, oz, dir.x, dir.y, dir.z, e.def.attackRange + 8, _wallHit)
    ) {
      this.audio.playHit('wall', { worldPos: _wallHit, volume: 0.4 });
      this.audio.playRicochet(_wallHit, eye);
    }
    this.audio.playAt(gunFireSfx(e.kind), { x: ox, y: oy, z: oz }, eye, {
      volume: 0.5,
      rate: 0.9 + Math.random() * 0.15,
      range: 56,
      refDistance: 8,
    });
  }
}

function eyeHeightFor(e: EnemyRuntime): number {
  return e.ducked ? 0.95 : 1.4;
}

function setEnemyCrouch(e: EnemyRuntime, world: CapsuleWorld, ducked: boolean): void {
  if (e.ducked === ducked) {
    // Keep height in sync if something else changed it
    const want = ducked ? e.duckHeight : e.standHeight;
    if (Math.abs(e.body.height - want) > 0.02) {
      if (!ducked && !world.canFitHeight(e.body, want)) return;
      e.body.height = want;
    }
    return;
  }
  if (!ducked && !world.canFitHeight(e.body, e.standHeight)) return;
  e.ducked = ducked;
  e.body.height = ducked ? e.duckHeight : e.standHeight;
}

function clearCover(e: EnemyRuntime): void {
  e.coverBrushId = null;
  e.coverWanted = false;
  e.coverT = 0;
  e.coverIsShort = false;
  e.coverCheckT = 0;
}

function isCoverBrush(brush: Brush): boolean {
  const id = brush.id;
  return (
    id.startsWith('crate') ||
    id.startsWith('sandbag') ||
    id.startsWith('desk') ||
    id.startsWith('pillar') ||
    id.startsWith('cabinet')
  );
}

/** Push a spawn capsule off desks/pillars if the authored point is solid. */
function findClearSpawn(
  world: CapsuleWorld,
  body: CapsuleBody,
  height: number,
): THREE.Vector3 | null {
  const origin = body.position.clone();
  if (world.canFitHeight(body, height)) return origin;
  const step = 0.4;
  const maxR = 2.4;
  for (let r = step; r <= maxR + 1e-6; r += step) {
    const n = Math.max(8, Math.round((Math.PI * 2 * r) / step));
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      body.position.set(origin.x + Math.cos(a) * r, origin.y, origin.z + Math.sin(a) * r);
      if (world.canFitHeight(body, height)) return body.position.clone();
    }
  }
  body.position.copy(origin);
  return null;
}

/** GE dodge space test — room to the side without walking into cover brushes. */
function lateralSpaceClear(
  world: CapsuleWorld,
  from: THREE.Vector3,
  toPlayer: THREE.Vector3,
  sign: number,
  meters: number,
): boolean {
  const sx = -toPlayer.z * sign;
  const sz = toPlayer.x * sign;
  const len = Math.hypot(sx, sz) || 1;
  const dx = (sx / len) * meters;
  const dz = (sz / len) * meters;
  // Sample along the sidestep — reject if we clip a solid brush footprint
  const steps = 6;
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const x = from.x + dx * t;
    const z = from.z + dz * t;
    for (const brush of world.brushes) {
      if (brush.id.startsWith('floor') || brush.id.startsWith('door_leaf')) continue;
      brushAabb(brush, _sideAabb);
      // Only care about solids that block footwork
      if (_sideAabb.max.y < from.y + 0.3) continue;
      if (
        x >= _sideAabb.min.x - 0.2 &&
        x <= _sideAabb.max.x + 0.2 &&
        z >= _sideAabb.min.z - 0.2 &&
        z <= _sideAabb.max.z + 0.2 &&
        from.y + 0.9 >= _sideAabb.min.y &&
        from.y + 0.2 <= _sideAabb.max.y
      ) {
        return false;
      }
    }
  }
  return true;
}

const _sideAabb = { min: new THREE.Vector3(), max: new THREE.Vector3() };

function isShortCover(brush: Brush): boolean {
  return (
    brush.id.startsWith('sandbag') ||
    brush.id.startsWith('desk') ||
    brush.size.y < 1.35
  );
}

interface CoverSlot {
  brushId: string;
  hide: THREE.Vector3;
  /** First pathable hop (may equal hide). */
  approach: THREE.Vector3;
  peek: THREE.Vector3;
  shortCover: boolean;
}

const _coverAabb = { min: new THREE.Vector3(), max: new THREE.Vector3() };
const _pathAabb = { min: new THREE.Vector3(), max: new THREE.Vector3() };
const _hideProbe = new THREE.Vector3();
const _peekProbe = new THREE.Vector3();
const _hopProbe = new THREE.Vector3();

/** True if the ground segment from→to passes through the brush footprint. */
function segmentHitsBrush(from: THREE.Vector3, to: THREE.Vector3, brush: Brush): boolean {
  brushAabb(brush, _pathAabb);
  // Expand slightly so grazing the collider counts as blocked
  const pad = 0.12;
  const minX = _pathAabb.min.x - pad;
  const maxX = _pathAabb.max.x + pad;
  const minZ = _pathAabb.min.z - pad;
  const maxZ = _pathAabb.max.z + pad;
  const steps = 12;
  for (let i = 1; i < steps; i++) {
    const t = i / steps;
    const x = from.x + (to.x - from.x) * t;
    const z = from.z + (to.z - from.z) * t;
    if (x >= minX && x <= maxX && z >= minZ && z <= maxZ) return true;
  }
  return false;
}

function coverRing(cx: number, cz: number, pad: number): Array<[number, number]> {
  return [
    [cx - pad, cz],
    [cx + pad, cz],
    [cx, cz - pad],
    [cx, cz + pad],
    [cx - pad, cz - pad],
    [cx + pad, cz - pad],
    [cx - pad, cz + pad],
    [cx + pad, cz + pad],
    [cx - pad * 0.75, cz - pad * 0.75],
    [cx + pad * 0.75, cz - pad * 0.75],
    [cx - pad * 0.75, cz + pad * 0.75],
    [cx + pad * 0.75, cz + pad * 0.75],
  ];
}

/** Peek stand point near hide with LOS to the player. */
function pickPeekPos(
  ray: THREE.Raycaster,
  world: CapsuleWorld,
  hide: THREE.Vector3,
  playerPos: THREE.Vector3,
  eyeH: number,
): THREE.Vector3 | null {
  const toPx = playerPos.x - hide.x;
  const toPz = playerPos.z - hide.z;
  const plen = Math.hypot(toPx, toPz) || 1;
  const fx = toPx / plen;
  const fz = toPz / plen;
  const lx = -fz;
  const lz = fx;
  const forwards = [0.35, 0.55, 0.8];
  const sides = [0, 1, -1, 1.4, -1.4];
  for (const f of forwards) {
    for (const side of sides) {
      _peekProbe.set(
        hide.x + fx * f + lx * PEEK_OFFSET * side,
        hide.y,
        hide.z + fz * f + lz * PEEK_OFFSET * side,
      );
      if (hasLineOfSight(ray, world, _peekProbe, playerPos, eyeH)) {
        return _peekProbe.clone();
      }
    }
  }
  return null;
}

/** Next hop around a brush toward hide (direct, else best ring waypoint). */
function nextCoverHop(
  from: THREE.Vector3,
  hide: THREE.Vector3,
  brush: Brush,
  y: number,
): THREE.Vector3 | null {
  if (!segmentHitsBrush(from, hide, brush)) {
    return hide.clone();
  }
  brushAabb(brush, _coverAabb);
  const cx = brush.center.x;
  const cz = brush.center.z;
  const hx = (_coverAabb.max.x - _coverAabb.min.x) * 0.5;
  const hz = (_coverAabb.max.z - _coverAabb.min.z) * 0.5;
  const pad = Math.max(hx, hz) + 0.7;
  let best: THREE.Vector3 | null = null;
  let bestScore = Infinity;
  for (const [x, z] of coverRing(cx, cz, pad)) {
    _hopProbe.set(x, y, z);
    if (segmentHitsBrush(from, _hopProbe, brush)) continue;
    // Prefer hops that clear a path toward hide, or at least get closer
    const clearsToHide = !segmentHitsBrush(_hopProbe, hide, brush);
    const distVia =
      Math.hypot(from.x - x, from.z - z) + Math.hypot(x - hide.x, z - hide.z);
    const score = distVia - (clearsToHide ? 4 : 0);
    if (score < bestScore) {
      bestScore = score;
      if (!best) best = _hopProbe.clone();
      else best.copy(_hopProbe);
    }
  }
  return best;
}

/** Pick a hide point with no LOS, a peek with LOS, and a pathable first hop. */
function findCoverSlot(
  world: CapsuleWorld,
  from: THREE.Vector3,
  playerPos: THREE.Vector3,
  ray: THREE.Raycaster,
): CoverSlot | null {
  let best: CoverSlot | null = null;
  let bestScore = -Infinity;

  for (const brush of world.brushes) {
    if (!isCoverBrush(brush)) continue;
    brushAabb(brush, _coverAabb);
    const cx = brush.center.x;
    const cz = brush.center.z;
    const hx = (_coverAabb.max.x - _coverAabb.min.x) * 0.5;
    const hz = (_coverAabb.max.z - _coverAabb.min.z) * 0.5;
    const pad = Math.max(hx, hz) + 0.65;
    const shortCover = isShortCover(brush);
    const ring = coverRing(cx, cz, pad);

    for (const [hxw, hzw] of ring) {
      _hideProbe.set(hxw, from.y, hzw);
      const distFromEnemy = Math.hypot(_hideProbe.x - from.x, _hideProbe.z - from.z);
      if (distFromEnemy > COVER_SEARCH_RANGE || distFromEnemy < 0.45) continue;

      const hidden = !hasLineOfSight(ray, world, _hideProbe, playerPos, 1.4);
      if (!hidden) continue;

      const approach = nextCoverHop(from, _hideProbe, brush, from.y);
      if (!approach) continue;
      // First hop must be pathable from here (nextCoverHop guarantees that)
      const pathLen =
        Math.hypot(from.x - approach.x, from.z - approach.z) +
        Math.hypot(approach.x - _hideProbe.x, approach.z - _hideProbe.z);

      // Peek: step toward player from hide, plus a lateral nudge
      const toPx = playerPos.x - _hideProbe.x;
      const toPz = playerPos.z - _hideProbe.z;
      const plen = Math.hypot(toPx, toPz) || 1;
      const fx = toPx / plen;
      const fz = toPz / plen;
      const lx = -fz;
      const lz = fx;

      let peekOk = false;
      for (const side of [1, -1, 0]) {
        _peekProbe.set(
          _hideProbe.x + fx * 0.55 + lx * PEEK_OFFSET * side,
          from.y,
          _hideProbe.z + fz * 0.55 + lz * PEEK_OFFSET * side,
        );
        if (hasLineOfSight(ray, world, _peekProbe, playerPos, 1.4)) {
          peekOk = true;
          break;
        }
      }
      if (!peekOk) continue;

      // Prefer shorter walk; bias tall cover and direct approaches
      const direct = approach.distanceToSquared(_hideProbe) < 0.01;
      const score = 16 - pathLen + (shortCover ? 0 : 1.2) + (direct ? 1.5 : 0);
      if (score > bestScore) {
        bestScore = score;
        if (!best) {
          best = {
            brushId: brush.id,
            hide: _hideProbe.clone(),
            approach: approach.clone(),
            peek: _peekProbe.clone(),
            shortCover,
          };
        } else {
          best.brushId = brush.id;
          best.hide.copy(_hideProbe);
          best.approach.copy(approach);
          best.peek.copy(_peekProbe);
          best.shortCover = shortCover;
        }
      }
    }
  }
  return best;
}

function gunWeaponId(kind: EnemyKind): WeaponId | null {
  if (
    kind === 'pistol' ||
    kind === 'shotgun' ||
    kind === 'mp5' ||
    kind === 'ak47' ||
    kind === 'sawedoff'
  ) {
    return kind;
  }
  return null;
}

function hideEnemyFpsHold(e: EnemyRuntime): void {
  if (!e.fpsHold) return;
  syncFpsWorldHold(e.fpsHold, {
    visible: false,
    ads: e.fpsAds,
    punch: null,
    lookPitch: 0,
    kneelBlend: e.kneelBlend,
    bones: e.model.userData.procBones as Record<string, THREE.Object3D> | undefined,
    parent: e.root,
  });
}

function enemyWantAds(e: EnemyRuntime): number {
  if (!gunWeaponId(e.kind)) return 1;
  if (e.attackPhase === 'aim' || e.attackPhase === 'shoot' || e.adsHoldT > 0) return 1;
  return 0;
}

function shouldShowFpsHold(e: EnemyRuntime): boolean {
  if (e.dead || e.ragdoll) return false;
  if (e.sitBlend > 0.45) return true;
  if (gunWeaponId(e.kind)) return true;
  const upper = e.layerMode === 'aimloco' ? e.upperClip : e.currentClip;
  if (e.attackPhase === 'swing' || e.attackPhase === 'windup' || upper === 'melee') return true;
  if (upper === 'guard') return true;
  return false;
}

function enemyPunchPose(
  e: EnemyRuntime,
): { id: EnemyRuntime['punchId']; u: number } | 'guard' | 'rest' | null {
  if (gunWeaponId(e.kind)) return null;
  if (e.sitBlend > 0.45) return 'rest';
  const upper = e.layerMode === 'aimloco' ? e.upperClip : e.currentClip;
  if (e.attackPhase === 'swing' || e.attackPhase === 'windup' || upper === 'melee') {
    return { id: e.punchId, u: punchProgress(e.punchId, e.attackLockT, e.attackPhase) };
  }
  return 'guard';
}

function updateEnemyFpsHold(e: EnemyRuntime, dt: number): void {
  if (!e.fpsHold) return;
  const visible = shouldShowFpsHold(e);
  const wantAds = e.sitBlend > 0.45 ? 0 : enemyWantAds(e);
  e.fpsAds += (wantAds - e.fpsAds) * (1 - Math.exp(-10 * dt));
  syncFpsWorldHold(e.fpsHold, {
    visible,
    ads: e.fpsAds,
    punch: visible ? enemyPunchPose(e) : null,
    lookPitch: -e.lookPitch,
    kneelBlend: Math.max(e.kneelBlend, e.sitBlend),
    bones: e.model.userData.procBones as Record<string, THREE.Object3D> | undefined,
    parent: e.root,
  });
}

function gunFireSfx(kind: EnemyKind): string {
  if (kind === 'shotgun' || kind === 'sawedoff') return 'shotgun';
  if (kind === 'mp5') return Math.random() < 0.5 ? 'mp5' : 'mp5b';
  if (kind === 'ak47') return Math.random() < 0.5 ? 'ak47' : 'ak47b';
  return Math.random() < 0.5 ? 'pistol' : 'pistol2';
}

function isAutoRifle(kind: EnemyKind): boolean {
  return kind === 'mp5' || kind === 'ak47';
}

function holdAimMove(e: EnemyRuntime): void {
  if (e.attackPhase !== 'aim') return;
  e.body.velocity.x *= 0.32;
  e.body.velocity.z *= 0.32;
}

function isFriendlyKind(kind: EnemyKind): boolean {
  return kind === 'scientist' || kind === 'civilian';
}

/** Farther shots are localized worse — they walk toward a guessed spot, not the muzzle. */
function guessHeardPos(origin: THREE.Vector3, dist: number, out: THREE.Vector3): void {
  const err = 1.1 + dist * 0.14;
  const ang = Math.random() * Math.PI * 2;
  const r = Math.random() * err;
  out.set(origin.x + Math.cos(ang) * r, origin.y, origin.z + Math.sin(ang) * r);
}

function pickRoamTarget(e: EnemyRuntime): void {
  const ang = Math.random() * Math.PI * 2;
  const r = 2 + Math.random() * (ROAM_RADIUS - 2);
  e.roamTarget.set(e.home.x + Math.cos(ang) * r, e.home.y, e.home.z + Math.sin(ang) * r);
}

function horizontalDist(a: THREE.Vector3, b: THREE.Vector3): number {
  const dx = a.x - b.x;
  const dz = a.z - b.z;
  return Math.hypot(dx, dz);
}

function hearDist(a: THREE.Vector3, b: THREE.Vector3): number {
  return a.distanceTo(b);
}

function inStallBay(p: THREE.Vector3, bayX: number): boolean {
  return (
    p.x > bayX - 1.2 &&
    p.x < bayX + 1.2 &&
    p.z > 1.15 &&
    p.z < 4.05 &&
    p.y > -0.2 &&
    p.y < 2.6
  );
}

function normalizeCharacter(model: THREE.Object3D): void {
  model.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(model);
  const size = box.getSize(new THREE.Vector3());
  const targetH = 1.78;
  if (size.y > 0.1) {
    const s = targetH / size.y;
    model.scale.multiplyScalar(s);
    model.updateMatrixWorld(true);
  }
  const box2 = new THREE.Box3().setFromObject(model);
  model.position.y -= box2.min.y;
}


function lateralSpeed(e: EnemyRuntime): number {
  return Math.hypot(e.body.velocity.x, e.body.velocity.z);
}

function aimLower(e: EnemyRuntime): 'idle' | 'walk' {
  const spd = lateralSpeed(e);
  if (e.aimLocoWalk) {
    if (spd < 0.22) e.aimLocoWalk = false;
  } else if (spd > 0.5) {
    e.aimLocoWalk = true;
  }
  return e.aimLocoWalk ? 'walk' : 'idle';
}

function applyCombatLife(e: EnemyRuntime, dt: number): void {
  if (e.state === 'hurt') return;
  e.breathT += dt;

  const spine = e.breathSpine;
  if (spine) {
    const aiming =
      e.currentClip.startsWith('aim') ||
      e.currentClip === 'idle' ||
      e.currentClip.startsWith('guard');
    const pitchAmp = aiming ? 0.026 : 0.014;
    const pitch = Math.sin(e.breathT * 1.7) * pitchAmp;
    const yaw = Math.sin(e.breathT * 1.15 + 0.4) * 0.01;
    const fwdX = -Math.sin(e.yaw);
    const fwdZ = -Math.cos(e.yaw);
    const lat = e.body.velocity.x * fwdZ - e.body.velocity.z * fwdX;
    const leanTarget = THREE.MathUtils.clamp(lat * 0.11, -0.07, 0.07);
    e.spineLean += (leanTarget - e.spineLean) * (1 - Math.exp(-8 * dt));
    spine.rotateX(pitch);
    spine.rotateY(yaw);
    spine.rotateZ(e.spineLean);
  }

  if (e.weapon && !e.fpsHold && e.kind !== 'melee' && e.weaponRecoil <= 0 && !e.frozen) {
    const bob = Math.sin(e.breathT * 2.1) * 0.003;
    e.weapon.position.set(
      e.weaponBasePos.x,
      e.weaponBasePos.y + bob,
      e.weaponBasePos.z + Math.cos(e.breathT * 1.6) * 0.002,
    );
  }
}

function combatLocked(e: EnemyRuntime, name: string): boolean {
  if (e.attackLockT <= 0 || e.attackPhase === 'none') return false;
  return (
    name !== 'shoot' &&
    name !== 'melee' &&
    name !== 'guard' &&
    name !== 'hit' &&
    name !== 'hurt_foot' &&
    name !== 'hurt_arm' &&
    name !== 'death' &&
    name !== 'aim' &&
    !(e.attackPhase === 'shoot' && (name === 'aim' || name.startsWith('aim')))
  );
}

function playClip(e: EnemyRuntime, name: string, _fade: number, loop: boolean): void {
  if (loop && e.layerMode === 'full' && e.currentClip === name) return;
  if (combatLocked(e, name)) return;
  e.layerMode = 'full';
  e.currentClip = name;
  e.upperClip = name;
  e.lowerClip = name;
}

function playLayered(e: EnemyRuntime, upperKey: string, lowerKey: string): void {
  const tag = `${upperKey}+${lowerKey}`;
  e.layerMode = 'aimloco';
  e.currentClip = tag;
  e.upperClip = upperKey;
  e.lowerClip = lowerKey;
}

function playMeleeLoco(e: EnemyRuntime, loco: 'walk' | 'run', _fade: number): void {
  if (e.attackLockT > 0 && e.attackPhase !== 'none') return;
  playLayered(e, 'guard', loco);
}

function playCombatClip(
  e: EnemyRuntime,
  preferred: string,
  fallbackAim: 'aim',
  fade: number,
  loop: boolean,
): void {
  if (e.actions.has(preferred)) {
    playClip(e, preferred, fade, loop);
    return;
  }
  playAimLoco(e, fallbackAim, 'idle', fade, true);
}

function playAimLoco(
  e: EnemyRuntime,
  upper: 'aim' | 'shoot',
  lower: 'idle' | 'walk',
  _fade: number,
  _upperLoop: boolean,
): void {
  if (e.attackLockT > 0 && e.attackPhase !== 'none') {
    if (
      (e.attackPhase === 'aim' || e.attackPhase === 'shoot') &&
      (upper === 'aim' || upper === 'shoot')
    ) {
      /* aim hold + fire use the aim pose */
    } else if (upper !== 'shoot') {
      return;
    }
  }
  playLayered(e, upper, lower);
}

/**
 * Close the support hand onto the gun after the mixer — layered walk aim was
 * leaving a visible gap vs the calib full-body pose.
 */
function applySupportHandClasp(e: EnemyRuntime): void {
  if (e.fpsHold || e.kind === 'melee' || e.dead || !e.weapon) return;
  const aiming =
    e.currentClip === 'aim' ||
    e.currentClip.startsWith('aim+') ||
    e.attackPhase === 'shoot' ||
    e.attackPhase === 'aim' ||
    e.state === 'duck' ||
    e.state === 'peek' ||
    e.state === 'cover' ||
    e.state === 'attack';
  if (!aiming) return;
  if (!e.leftHand || !e.leftForeArm || !e.leftArm || !e.rightHand) return;

  const forend =
    e.kind === 'shotgun' ? 0.28 : e.kind === 'ak47' ? 0.24 : e.kind === 'mp5' ? 0.18 : e.kind === 'sawedoff' ? 0.16 : 0;
  claspSupportHand(e.leftArm, e.leftForeArm, e.leftHand, e.rightHand, e.weapon, forend);
}

function findNamed(root: THREE.Object3D, name: string): THREE.Object3D | null {
  let found: THREE.Object3D | null = null;
  root.traverse((o) => {
    if (!found && o.name === name) found = o;
  });
  return found;
}

function headgearFromHit(object: THREE.Object3D | null): 'hat' | 'helmet' | null {
  let o: THREE.Object3D | null = object;
  while (o) {
    if (o.userData.headgear === 'hat' || o.userData.headgear === 'helmet') {
      return o.userData.headgear as 'hat' | 'helmet';
    }
    o = o.parent;
  }
  return null;
}

function findBone(root: THREE.Object3D, names: string[]): THREE.Object3D | null {
  // Exact bare-name match only — never substring (RightHand ≠ RightHandThumb1).
  const wants = names.map((n) =>
    n.toLowerCase().replace(/^mixamorig:/, '').replace(/^mixamorig/, ''),
  );
  let found: THREE.Object3D | null = null;
  root.traverse((o) => {
    if (found) return;
    const bare = o.name.toLowerCase().replace(/^mixamorig:/, '').replace(/^mixamorig/, '');
    for (const w of wants) {
      if (bare === w) {
        found = o;
        return;
      }
    }
  });
  return found;
}

function knockbackStrength(id: WeaponId | undefined): number {
  switch (id) {
    case 'shotgun':
      return 2.2;
    case 'sawedoff':
      return 2.7;
    case 'ak47':
      return 0.95;
    case 'mp5':
      return 0.8;
    default:
      return 0.7;
  }
}

function boneBare(name: string): string {
  return name.toLowerCase().replace(/^mixamorig:/, '').replace(/^mixamorig/, '');
}

function classifyBone(name: string): 'foot' | 'arm' | 'torso' | 'head' {
  const n = boneBare(name);
  if (n.includes('head') || n === 'neck' || n.includes('helmet') || n.includes('hat')) return 'head';
  if (n.includes('foot') || n.includes('toe') || n.endsWith('leg') || n.includes('upleg')) {
    // UpLeg/Leg are legs — foot zone includes lower leg as requested
    if (n.includes('foot') || n.includes('toe') || (n.includes('leg') && !n.includes('upleg'))) {
      return 'foot';
    }
  }
  if (
    n.includes('arm') ||
    n.includes('hand') ||
    n.includes('shoulder') ||
    n.includes('forearm')
  ) {
    if (!n.includes('thumb') && !n.includes('index') && !n.includes('middle') && !n.includes('ring') && !n.includes('pinky')) {
      return 'arm';
    }
    if (n.endsWith('hand')) return 'arm';
  }
  return 'torso';
}

function hitZoneFromPoint(e: EnemyRuntime, point: THREE.Vector3): 'foot' | 'arm' | 'torso' | 'head' {
  let bestName = '';
  let bestD = Infinity;
  e.model.updateMatrixWorld(true);
  e.model.traverse((o) => {
    if (!o.name) return;
    const n = boneBare(o.name);
    if (
      !n.includes('foot') &&
      !n.includes('toe') &&
      !n.includes('leg') &&
      !n.includes('arm') &&
      !n.includes('hand') &&
      !n.includes('shoulder') &&
      n !== 'hips' &&
      n !== 'spine' &&
      n !== 'spine1' &&
      n !== 'spine2' &&
      n !== 'head' &&
      n !== 'neck'
    ) {
      return;
    }
    o.getWorldPosition(_hitBone);
    const d = _hitBone.distanceToSquared(point);
    if (d < bestD) {
      bestD = d;
      bestName = o.name;
    }
  });
  if (!bestName) return 'torso';
  return classifyBone(bestName);
}

function nearestBone(e: EnemyRuntime, point: THREE.Vector3): THREE.Object3D | null {
  let best: THREE.Object3D | null = null;
  let bestD = Infinity;
  e.model.updateMatrixWorld(true);
  e.model.traverse((o) => {
    if (!(o as THREE.Bone).isBone) return;
    o.getWorldPosition(_hitBone);
    const d = _hitBone.distanceToSquared(point);
    if (d < bestD) {
      bestD = d;
      best = o;
    }
  });
  return best;
}

function spawnBloodMark(e: EnemyRuntime, point: THREE.Vector3 | undefined, _zone: string): void {
  if (!point) return;
  const bone = nearestBone(e, point) ?? e.model;
  bone.updateWorldMatrix(true, false);
  const geo = new THREE.CircleGeometry(0.032 + Math.random() * 0.02, 8);
  const mat = new THREE.MeshBasicMaterial({
    color: 0x6a050c,
    transparent: true,
    opacity: 0.92,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  const mark = new THREE.Mesh(geo, mat);
  mark.userData.ignoreHit = true;
  mark.userData.life = 7 + Math.random() * 3;
  bone.worldToLocal(_bloodLocal.copy(point));
  mark.position.copy(_bloodLocal);
  mark.rotation.set(Math.PI / 2, Math.random() * Math.PI, 0);
  bone.add(mark);
  e.bloodMarks.push(mark);
  while (e.bloodMarks.length > 8) {
    const old = e.bloodMarks.shift();
    if (!old) break;
    old.parent?.remove(old);
    old.geometry.dispose();
    (old.material as THREE.Material).dispose();
  }
}

function fadeBlood(e: EnemyRuntime, dt: number): void {
  for (let i = e.bloodMarks.length - 1; i >= 0; i--) {
    const m = e.bloodMarks[i];
    m.userData.life = (m.userData.life as number) - dt;
    const mat = m.material as THREE.MeshBasicMaterial;
    mat.opacity = Math.max(0, Math.min(0.92, (m.userData.life as number) * 0.12));
    if ((m.userData.life as number) <= 0) {
      m.parent?.remove(m);
      m.geometry.dispose();
      mat.dispose();
      e.bloodMarks.splice(i, 1);
    }
  }
}

function hurtClipDone(e: EnemyRuntime): boolean {
  const name = e.currentClip;
  if (name !== 'hurt_foot' && name !== 'hurt_arm' && name !== 'hit') return false;
  return e.hurtT <= 0.04;
}

function hurtDurationFor(_e: EnemyRuntime, zone: 'foot' | 'arm' | 'torso' | 'head'): number {
  return zone === 'torso' || zone === 'head' ? 0.4 : 0.55;
}

function restoreHurtLoco(e: EnemyRuntime): void {
  if (e.dead) return;
  if (e.kind === 'melee') {
    playClip(e, 'guard', FADE_COMBAT, true);
    return;
  }
  if (e.alerted) {
    playAimLoco(e, 'aim', aimLower(e), FADE_COMBAT, true);
    return;
  }
  playClip(e, lateralSpeed(e) > 0.25 ? 'walk' : 'idle', FADE_LOCO, true);
}

function playHurtReaction(e: EnemyRuntime, zone: 'foot' | 'arm' | 'torso' | 'head'): void {
  if (zone === 'foot' && e.actions.has('hurt_foot')) {
    playClip(e, 'hurt_foot', FADE_HURT, false);
    e.hurtOverlay = null;
    return;
  }
  if (zone === 'arm' && e.actions.has('hurt_arm')) {
    playClip(e, 'hurt_arm', FADE_HURT, false);
    e.hurtOverlay = null;
    return;
  }
  playClip(e, 'hit', FADE_HURT, false);
  if (zone === 'foot') {
    e.hurtOverlay = { zone: 'foot', side: 'R', t: 0, duration: 0.7 };
  } else if (zone === 'arm') {
    e.hurtOverlay = { zone: 'arm', side: 'L', t: 0, duration: 0.62 };
  } else {
    e.hurtOverlay = null;
  }
}

function applyHurtOverlay(e: EnemyRuntime, dt: number): void {
  const ov = e.hurtOverlay;
  if (!ov) return;
  ov.t += dt;
  const fade = 1 - Math.min(1, ov.t / ov.duration);
  const attack = Math.sin(Math.min(1, ov.t / 0.1) * Math.PI * 0.5);
  const w = attack * fade;
  if (ov.zone === 'foot') {
    const foot = e.rightFoot ?? e.leftFoot;
    if (foot) foot.rotateX(-0.95 * w);
    const hop = Math.sin(Math.min(1, ov.t / 0.32) * Math.PI) * 0.09 * fade;
    e.model.position.y = e.modelBaseY + hop;
  } else if (ov.zone === 'arm') {
    const arm = ov.side === 'L' ? e.leftArm : e.rightArm;
    const forearm = ov.side === 'L' ? e.leftForeArm : e.rightForeArm;
    if (arm) arm.rotateZ((ov.side === 'L' ? -0.9 : 0.9) * w);
    if (forearm) forearm.rotateX(-1.15 * w);
  }
  if (ov.t >= ov.duration) {
    e.hurtOverlay = null;
    e.model.position.y = e.modelBaseY;
  }
}

const _hitBone = new THREE.Vector3();
const _bloodLocal = new THREE.Vector3();

function syncRoot(e: EnemyRuntime): void {
  e.root.position.x = e.body.position.x;
  e.root.position.y = e.body.position.y;
  e.root.position.z = e.body.position.z;
  e.root.rotation.y = e.yaw;
  // Keep procedural duck sink applied (mixer / other paths may not touch model.y)
  if (e.modelBaseY !== undefined) {
    e.model.position.y = e.modelBaseY - e.duckSink;
  }
}

/** Mild local kick on the prop — replaces the unusable Kimodo shoot clip. */
function applyWeaponRecoil(e: EnemyRuntime, dt: number): void {
  if (!e.weapon || e.kind === 'melee') return;
  if (e.weaponRecoil <= 0) {
    // Keep socket in sync when calib/debug moves the gun while idle
    if (e.frozen) {
      e.weaponBasePos.copy(e.weapon.position);
      e.weaponBaseRot.set(
        e.weapon.rotation.x,
        e.weapon.rotation.y,
        e.weapon.rotation.z,
        e.weapon.rotation.order,
      );
    }
    return;
  }
  e.weaponRecoil = Math.max(0, e.weaponRecoil - dt * 7);
  const k = e.weaponRecoil * e.weaponRecoil;
  // Viewmodel frame: muzzle is −Z, so kick +Z (into the wrist) and up a little.
  e.weapon.position.set(
    e.weaponBasePos.x,
    e.weaponBasePos.y + k * 0.018,
    e.weaponBasePos.z + k * 0.045,
  );
  e.weapon.rotation.order = e.weaponBaseRot.order;
  e.weapon.rotation.set(
    e.weaponBaseRot.x - k * 0.14,
    e.weaponBaseRot.y,
    e.weaponBaseRot.z,
  );
}

/**
 * After mixer + root sync: reset head/neck to bind and apply clamped local aim
 * toward the player. Avoids Mixamo axis fights from world-space lookAt.
 */
function applyLookAt(e: EnemyRuntime, target: THREE.Vector3, dt: number): void {
  if (!e.lookHead || !e.alerted || e.dead || e.state === 'hurt') return;

  e.root.updateMatrixWorld(true);
  const headPos = e.lookHead.getWorldPosition(_lookTo);
  _lookFwd.subVectors(target, headPos);
  if (_lookFwd.lengthSq() < 1e-4) return;
  _lookFwd.normalize();

  _lookBodyFwd.set(-Math.sin(e.yaw), 0, -Math.cos(e.yaw));
  _lookFlat.set(_lookFwd.x, 0, _lookFwd.z);
  if (_lookFlat.lengthSq() < 1e-8) return;
  _lookFlat.normalize();

  let yawErr = Math.atan2(
    _lookFlat.x * _lookBodyFwd.z - _lookFlat.z * _lookBodyFwd.x,
    _lookFlat.dot(_lookBodyFwd),
  );
  yawErr = THREE.MathUtils.clamp(yawErr, -LOOK_YAW_MAX, LOOK_YAW_MAX);
  let pitch = Math.asin(THREE.MathUtils.clamp(_lookFwd.y, -1, 1));
  pitch = THREE.MathUtils.clamp(pitch, -LOOK_PITCH_MAX, LOOK_PITCH_MAX);

  const aiming =
    e.attackPhase === 'aim' ||
    e.attackPhase === 'shoot' ||
    e.upperClip === 'aim' ||
    e.upperClip === 'shoot' ||
    e.currentClip.startsWith('aim');
  const k = 1 - Math.exp(-(aiming ? LOOK_SPEED * 1.35 : LOOK_SPEED) * dt);
  e.lookYaw += (yawErr - e.lookYaw) * k;
  e.lookPitch += (pitch - e.lookPitch) * k;

  // Bind + local yaw/pitch (signs tuned for UniRig Mixamo head)
  e.lookHead.quaternion.copy(e.lookHeadBind);
  e.lookHead.rotateY(-e.lookYaw + LOOK_YAW_BIAS);
  e.lookHead.rotateX(-e.lookPitch);

  if (e.lookNeck) {
    e.lookNeck.quaternion.copy(e.lookNeckBind);
    e.lookNeck.rotateY(-e.lookYaw * LOOK_NECK_WEIGHT + LOOK_YAW_BIAS * LOOK_NECK_WEIGHT);
  }
}

const _losAabb = { min: new THREE.Vector3(), max: new THREE.Vector3() };
const _speechWorld = new THREE.Vector3();
const _speechNdc = new THREE.Vector3();
const _wallHit = new THREE.Vector3();
const _pelletHit = new THREE.Vector3();
const _muzzleWorld = new THREE.Vector3();

function enemyMuzzleWorld(e: EnemyRuntime, fallback: THREE.Vector3): THREE.Vector3 {
  const tip = e.weapon?.getObjectByName('muzzle') ?? e.weapon;
  if (!tip) return fallback;
  tip.updateWorldMatrix(true, false);
  return tip.getWorldPosition(_muzzleWorld);
}

function canSeePlayer(
  e: EnemyRuntime,
  toPlayer: THREE.Vector3,
  dist: number,
  los: boolean,
  playerCrouched: boolean,
): boolean {
  if (!los) return false;
  if (dist < SIGHT_BUMP) return true;
  const range = playerCrouched ? SIGHT_RANGE_CROUCH : SIGHT_RANGE;
  if (dist > range) return false;
  const fx = -Math.sin(e.yaw);
  const fz = -Math.cos(e.yaw);
  return fx * toPlayer.x + fz * toPlayer.z >= SIGHT_COS;
}

function footstepHearRange(player: PlayerController): number {
  const spd = Math.hypot(player.body.velocity.x, player.body.velocity.z);
  if (player.isSliding || spd > 7.8) return HEAR_SPRINT;
  if (player.crouchBlend > 0.5) return spd > 0.35 ? HEAR_CROUCH : 0;
  if (spd < 0.45) return 0;
  return HEAR_WALK;
}

function losIgnores(id: string): boolean {
  return id.startsWith('glass') || id.startsWith('safety') || id.startsWith('hazard');
}

function aabbContains(
  x: number,
  y: number,
  z: number,
  aabb: { min: THREE.Vector3; max: THREE.Vector3 },
): boolean {
  return (
    x >= aabb.min.x &&
    x <= aabb.max.x &&
    y >= aabb.min.y &&
    y <= aabb.max.y &&
    z >= aabb.min.z &&
    z <= aabb.max.z
  );
}

/** Segment vs AABB (slab). Walls, floors, and ceilings block; glass does not. */
function segmentHitsAabb(
  ax: number,
  ay: number,
  az: number,
  bx: number,
  by: number,
  bz: number,
  aabb: { min: THREE.Vector3; max: THREE.Vector3 },
): boolean {
  let t0 = 0;
  let t1 = 1;
  const dx = bx - ax;
  const dy = by - ay;
  const dz = bz - az;
  const ox = [ax, ay, az];
  const d = [dx, dy, dz];
  const mn = [aabb.min.x, aabb.min.y, aabb.min.z];
  const mx = [aabb.max.x, aabb.max.y, aabb.max.z];
  for (let i = 0; i < 3; i++) {
    if (Math.abs(d[i]) < 1e-12) {
      if (ox[i] < mn[i] || ox[i] > mx[i]) return false;
      continue;
    }
    let ta = (mn[i] - ox[i]) / d[i];
    let tb = (mx[i] - ox[i]) / d[i];
    if (ta > tb) {
      const tmp = ta;
      ta = tb;
      tb = tmp;
    }
    t0 = Math.max(t0, ta);
    t1 = Math.min(t1, tb);
    if (t0 > t1) return false;
  }
  return true;
}

function hasLineOfSight(
  _ray: THREE.Raycaster,
  world: CapsuleWorld,
  from: THREE.Vector3,
  to: THREE.Vector3,
  fromEyeH = 1.4,
  toEyeH = 1.4,
): boolean {
  const ax = from.x;
  const ay = from.y + fromEyeH;
  const az = from.z;
  const bx = to.x;
  const by = to.y + toEyeH;
  const bz = to.z;
  const len = Math.hypot(bx - ax, by - ay, bz - az);
  if (len < 0.1) return true;
  for (const brush of world.brushes) {
    if (brush.noCollision || losIgnores(brush.id)) continue;
    brushAabb(brush, _losAabb);
    // Eye can sit inside a stairwell ceiling / thick landing — don't self-occlude
    if (aabbContains(ax, ay, az, _losAabb) || aabbContains(bx, by, bz, _losAabb)) continue;
    if (segmentHitsAabb(ax, ay, az, bx, by, bz, _losAabb)) return false;
  }
  return true;
}

/** Closest wall along a shot ray. Returns false if the pellet flies into open space. */
function firstWallHit(
  world: CapsuleWorld,
  ox: number,
  oy: number,
  oz: number,
  dx: number,
  dy: number,
  dz: number,
  maxDist: number,
  out: THREE.Vector3,
): boolean {
  let bestT = maxDist;
  let hit = false;
  for (const brush of world.brushes) {
    if (brush.noCollision || losIgnores(brush.id)) continue;
    brushAabb(brush, _losAabb);
    const t = rayAabbEnterT(ox, oy, oz, dx, dy, dz, _losAabb);
    if (t != null && t > 0.08 && t < bestT) {
      bestT = t;
      hit = true;
    }
  }
  if (hit) out.set(ox + dx * bestT, oy + dy * bestT, oz + dz * bestT);
  return hit;
}

function rayAabbEnterT(
  ox: number,
  oy: number,
  oz: number,
  dx: number,
  dy: number,
  dz: number,
  aabb: { min: THREE.Vector3; max: THREE.Vector3 },
): number | null {
  let t0 = 0;
  let t1 = 1e9;
  const o = [ox, oy, oz];
  const d = [dx, dy, dz];
  const mn = [aabb.min.x, aabb.min.y, aabb.min.z];
  const mx = [aabb.max.x, aabb.max.y, aabb.max.z];
  for (let i = 0; i < 3; i++) {
    if (Math.abs(d[i]!) < 1e-8) {
      if (o[i]! < mn[i]! || o[i]! > mx[i]!) return null;
      continue;
    }
    let ta = (mn[i]! - o[i]!) / d[i]!;
    let tb = (mx[i]! - o[i]!) / d[i]!;
    if (ta > tb) {
      const tmp = ta;
      ta = tb;
      tb = tmp;
    }
    t0 = Math.max(t0, ta);
    t1 = Math.min(t1, tb);
    if (t0 > t1) return null;
  }
  return t0;
}
